import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * Help function which tests whether the tool implements
 * getMapForm function of the IMapFormControl interface.
 *
 * @param tool
 */
function instanceOfMapForm(tool) {
    return tool.getMapForm !== undefined;
}

/**
 * Help function which tests whether the tool implements
 * getMapForm function of the IMapFormControl interface.
 *
 * @param tool
 */
function instanceOfMapLegend(tool) {
    return tool.getMapLegend !== undefined;
}

/**
 * This enum defines GeoJSON object types.
 *
 * @author Jiri Hynek
 */
var GeoJSONTypes;
(function (GeoJSONTypes) {
    GeoJSONTypes["Point"] = "Point";
    GeoJSONTypes["MultiPoint"] = "MultiPoint";
    GeoJSONTypes["LineString"] = "LineString";
    GeoJSONTypes["MultiLineString"] = "MultiLineString";
    GeoJSONTypes["Polygon"] = "Polygon";
    GeoJSONTypes["MultiPolygon"] = "MultiPolygon";
    GeoJSONTypes["GeometryCollection"] = "GeometryCollection";
    GeoJSONTypes["Feature"] = "Feature";
    GeoJSONTypes["FeatureCollection"] = "FeatureCollection";
})(GeoJSONTypes || (GeoJSONTypes = {}));
var GeoJSONTypes$1 = GeoJSONTypes;

var LayerToolRenderType;
(function (LayerToolRenderType) {
    LayerToolRenderType[LayerToolRenderType["LAYER"] = 1] = "LAYER";
    LayerToolRenderType[LayerToolRenderType["DATA"] = 2] = "DATA";
    LayerToolRenderType[LayerToolRenderType["STYLE"] = 3] = "STYLE";
})(LayerToolRenderType || (LayerToolRenderType = {}));
var LayerToolRenderType$1 = LayerToolRenderType;

/**
 * The abstract implementation of map domain which overrides toString function.
 *
 * @author Jiri Hynek
 */
class AbstractMapDomain {
    /**
     * The string representation is equal to the name of the map domain.
     */
    toString() {
        return this.getName();
    }
}

/**
 * The generic implementation of map domain which overrides toString function.
 *
 * @author Jiri Hynek
 */
class MapDomain extends AbstractMapDomain {
    constructor(name) {
        super();
        this.name = name;
    }
    /**
     * It returns the name of the map domain.
     */
    getName() {
        return this.name;
    }
}

/**
 * This class provides the sum aggregation function.
 *
 * @author Jiri Hynek
 */
class CountAggregationFunction extends MapDomain {
    /**
     * It initializes the function.
     */
    constructor() {
        super(CountAggregationFunction.TYPE());
    }
    /**
     * Type of the function.
     */
    static TYPE() {
        return "count";
    }
    /**
     * It returns a aggregation bucket for aggregation of multiple values.
     */
    getAggregationBucket() {
        return new class {
            constructor() {
                this.count = 0;
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            addValue(value) {
                this.count++;
            }
            getValue() {
                return this.count;
            }
        }();
    }
}

/**
 * This class provides the sum aggregation function.
 *
 * @author Jiri Hynek
 */
class SumAggregationFunction extends MapDomain {
    /**
     * It initializes the function.
     */
    constructor() {
        super(SumAggregationFunction.TYPE());
    }
    /**
     * Type of the function.
     */
    static TYPE() {
        return "sum";
    }
    /**
     * It returns a aggregation bucket for aggregation of multiple values.
     */
    getAggregationBucket() {
        return new class {
            constructor() {
                this.sum = 0;
            }
            addValue(value) {
                this.sum += value;
            }
            getValue() {
                return this.sum;
            }
        }();
    }
}

/**
 * This class provides a generic aggregation function.
 *
 * @author Jiri Hynek
 */
class MapAggregationFunction extends MapDomain {
    /**
     * It initializes the function.
     */
    constructor(type, aggregationBucket) {
        super(type);
        this.aggregationBucket = aggregationBucket;
    }
    /**
     * It returns a aggregation bucket for aggregation of multiple values.
     */
    getAggregationBucket() {
        return this.aggregationBucket();
    }
}

/**
 * This class provides a factory for aggregation functions.
 *
 * @author Jiri Hynek
 */
class MapAggregationFunctionFactory$2 {
    /**
     * It creates a generic aggregation function.
     */
    default(type, aggregationBucket) {
        return new MapAggregationFunction(type, aggregationBucket);
    }
    /**
     * It creates the count aggregation function.
     */
    count() {
        return new CountAggregationFunction();
    }
    /**
     * It creates the sum aggregation function.
     */
    sum() {
        return new SumAggregationFunction();
    }
}

/**
 * This class specifies the empty type constraint.
 *
 * @author Jiri Hynek
 */
class BooleanTypeManager {
    /**
     * It converts the string representation of the boolean value.
     */
    deserialize(value) {
        return value === "true";
    }
}

/**
 * This class specifies the integer type manager.
 *
 * @author Jiri Hynek
 */
class IntegerTypeManager {
    /**
     * It converts the string representation of the number value.
     */
    deserialize(value) {
        return Number.parseInt(value);
    }
}

/**
 * This class specifies the integer type manager.
 *
 * @author Jiri Hynek
 */
class IntegerRangeManager extends IntegerTypeManager {
    constructor(min, max) {
        super();
        this.min = min;
        this.max = max;
    }
    /**
     * It returns the min value.
     */
    getMinValue() {
        return this.min;
    }
    /**
     * It returns the max value.
     */
    getMaxValue() {
        return this.max;
    }
}

/**
 * The interface declares function for management of geographical data.
 *
 * @author Jiri Hynek
 */
class GeoJsonData extends MapDomain {
    constructor(name, originalData) {
        super(name);
        this.originalData = originalData;
    }
    /**
     * It returns the original source of geographical data.
     */
    getOriginalGeoData() {
        return this.originalData;
    }
    /**
     * It returns the original representation of data domain.
     */
    getGeoJSON() {
        if (!this.geoJSON) {
            this.geoJSON = this.validateData(this.originalData);
        }
        return this.geoJSON;
    }
    /**
     * It returns the array of features of specific type.
     *
     * @param types
     */
    getFeatures(types) {
        const featureCollection = {
            type: GeoJSONTypes$1.FeatureCollection,
            features: []
        };
        const geoJSON = this.getGeoJSON();
        for (const f of geoJSON.features) {
            if (f.geometry && types.includes(f.geometry.type)) {
                featureCollection.features.push(f);
            }
        }
        return featureCollection;
    }
    /**
     * It transforms original data to a feature collection in the GeoJSON format.
     *
     * @param originalData
     * @returns GeoJSON feature collection
     */
    validateData(originalData) {
        // TODO: provide validation and transformation
        return originalData;
    }
}

/**
 * This interface declares a factory for geographical data objects.
 *
 * @author Jiri Hynek
 */
class GeoDataFactory {
    /**
     * It creates the GeoJSON geographical data object.
     */
    geojson(name, data) {
        return new GeoJsonData(name, data);
    }
}

/**
 * Class representing definition of hierarchy for one domain of geographical objects.
 * Used as intermediary between hierarchy tool and map layers, present only in GeoDataManager object.
 *
 * @author Vojtěch Malý
 */
class HierarchyTree {
    constructor(name, aggregationFlag = false) {
        this.Tree = new Map(); // Hierarchy Tree data.
        this.zoomLevels = []; // Sorted array of number's representing all zoom levels in which active objects change.
        this.active = []; // Array of acitve nodeID's objects. 
        this.roots = [];
        this.changeFlag = false;
        this.name = name;
        this.aggregationFlag = aggregationFlag;
    }
    /**
     * Creates new node in tree
     *
     * @param nodeID                    nodeID of node
     * @param parentID              nodeID of parent
     * @param zoomLevel             Zoom level where node gets active
     */
    initByNode(nodeID, zoomLevel) {
        this.Tree.set(nodeID, {
            nodeID: nodeID,
            childsID: [],
            zoomLevel: zoomLevel,
            isParent: false
        });
        this.updateZoomLevels(zoomLevel);
    }
    /**
     * Creates new root node. There may be more than one root node,
     * but they must not have any more parents.
     *
     * @param nodeID
     * @param zoomLevel
     */
    initByRoot(nodeID, zoomLevel) {
        this.Tree.set(nodeID, {
            nodeID: nodeID,
            childsID: [],
            zoomLevel: zoomLevel,
            isParent: true
        });
        this.updateZoomLevels(zoomLevel);
        this.roots.push(nodeID);
    }
    /**
     * Creates tree structure from [nodeID, ParentID, Zoomlevel] array.
     *
     * @param nodes - Array of nodes. *There is no need for particular order.
     */
    initByArray(nodes) {
        const unresolved = new Map(); // Map of unresolved nodes. (Node has parent, that is not yet inicialized.)
        nodes.forEach(node => {
            var _a, _b, _c, _d;
            if (typeof node[1] === "boolean") { // Root inicialization.
                this.initByRoot(node[0], node[2]);
                if (unresolved.has(node[0])) { // Resolving possible unresolved childs
                    const arrayOfChilds = (_a = unresolved.get(node[0])) !== null && _a !== void 0 ? _a : [];
                    arrayOfChilds.forEach(child => {
                        var _a;
                        (_a = this.Tree.get(node[0])) === null || _a === void 0 ? void 0 : _a.childsID.push(child);
                    });
                    unresolved.delete(node[0]);
                }
            }
            else { // Node init
                if (this.Tree.has(node[1])) { // Parent is already in Tree
                    //this.Tree.get(node[1])?.addChild(node[0]);
                    (_b = this.Tree.get(node[1])) === null || _b === void 0 ? void 0 : _b.childsID.push(node[0]);
                }
                else { // Parent is not in tree yet.
                    const temp = (_c = unresolved.get(node[1])) !== null && _c !== void 0 ? _c : [];
                    temp.push(node[0]);
                    unresolved.set(node[1], temp);
                }
                this.initByNode(node[0], node[2]); // Create node        
                if (unresolved.has(node[0])) { // Resolving unresolved childs
                    const arrayOfChilds = (_d = unresolved.get(node[0])) !== null && _d !== void 0 ? _d : [];
                    arrayOfChilds.forEach(child => {
                        var _a;
                        (_a = this.Tree.get(node[0])) === null || _a === void 0 ? void 0 : _a.childsID.push(child);
                    });
                    unresolved.delete(node[0]);
                }
            }
        });
    }
    /**
     * Initialize first zoom level.
     *
     * @param zoom
     */
    startActiveWatch(zoom) {
        this.active = this.getActiveByZoom(zoom);
    }
    /**
     * Update Zoom Levels array
     *
     * @param zoomLevel
     */
    updateZoomLevels(zoomLevel) {
        if (!this.zoomLevels.includes(zoomLevel)) {
            this.zoomLevels.push(zoomLevel);
            this.zoomLevels.sort();
        }
    }
    /**
     * Returns nodeID array of geoobjects visible in that particular zoom.
     *
     * @param zoom
     * @returns
     */
    getActiveByZoom(zoom) {
        var _a;
        const resolverArray = [];
        this.roots.forEach(value => { resolverArray.push(value); });
        let answer = [];
        while (resolverArray.length > 0) {
            const resolved = resolverArray.pop();
            if (!resolved) {
                continue;
            }
            const node = (_a = this.Tree.get(resolved)) !== null && _a !== void 0 ? _a : undefined;
            if (node) {
                if (node.zoomLevel >= zoom) {
                    answer.push(node.nodeID);
                }
                else {
                    if (node.childsID.length < 1) { // Does not have any childs, is active even under his active zoom.
                        answer.push(node.nodeID);
                    }
                    else {
                        node.childsID.forEach(child => {
                            resolverArray.push(child);
                        });
                    }
                }
            }
        }
        // If empty, return parents beacause nothing in tree changed.
        if (answer.length == 0) {
            answer = this.roots;
        }
        return answer;
    }
    /**
     * Returns all lowest chidls of that object.
     *
     * @param root - nodeID of node in tree.
     * @returns - Array of lowest ids.
     */
    getAllLeafes(root) {
        var _a;
        const answer = [];
        const reslover = [];
        reslover.push(root);
        while (reslover.length > 0) {
            const resolving = reslover.pop();
            if (resolving) {
                if (this.Tree.has(resolving)) {
                    const node = this.Tree.get(resolving);
                    const length = (_a = node === null || node === void 0 ? void 0 : node.childsID.length) !== null && _a !== void 0 ? _a : 0;
                    if (length > 0) {
                        node === null || node === void 0 ? void 0 : node.childsID.forEach(child => {
                            reslover.push(child);
                        });
                    }
                    else {
                        answer.push(resolving);
                    }
                }
            }
        }
        return answer;
    }
    /**
     * Recalculates visible objects.
     * If there was change, sets change flag to true until next recalculation.
     *
     * @param newZoom
     * @returns
     */
    recalculate(newZoom) {
        if (this.changeFlag) {
            this.changeFlag = false;
        }
        const newActive = this.getActiveByZoom(newZoom).sort();
        const oldActive = this.active.sort();
        let flag = false;
        // Length check
        if (newActive.length != oldActive.length) {
            flag = true;
        }
        else {
            for (let cnt = 0; cnt < newActive.length; cnt++) {
                if (newActive[cnt] != oldActive[cnt]) {
                    flag = true;
                    break;
                }
            }
        }
        if (flag) {
            this.changeFlag = true;
            this.active = newActive;
        }
        return this.changeFlag;
    }
    /**
     * @returns Change flag
     */
    getChangeFlag() {
        return this.changeFlag;
    }
    /**
     * @returns Active objects of tree
     */
    getActive() {
        return this.active;
    }
    /**
     * @returns Aggregation flag status
     */
    getAggregationFlag() {
        return this.aggregationFlag;
    }
    /**
     * Sets aggregration flag
     * @param flag True if enabled
     */
    setAggregationFlag(flag) {
        this.aggregationFlag = flag;
    }
}

/**
 * This class provide functions for using map domains which can be identified by uniquie string.
 *
 * @author Jiri Hynek
 */
class MapDomainArrayManager {
    /**
     * It initializes a map domain manager.
     */
    constructor(domains) {
        this.domains = domains ? domains : [];
    }
    /**
     * The function returns available map domains.
     */
    getDomains() {
        return this.domains;
    }
    /**
     * The function returns the first domain of the array.
     */
    getDefault() {
        const domains = this.getDomains();
        return domains && domains.length > 0 ? domains[0] : undefined;
    }
    /**
     * The function returns number of domains.
     */
    size() {
        return this.domains.length;
    }
    /**
     * The function returns true if size() is 0.
     */
    isEmpty() {
        return this.domains.length == 0;
    }
    /**
     * It adds a domain to the list of domains.
     *
     * Override this function.
     *
     * @param domains
     */
    add(domains) {
        this.domains.push(domains);
    }
    /**
     * It removes a domain from the list of domains.
     *
     * @param domains
     */
    remove(domains) {
        this.domains = this.domains.filter(item => item != domains);
    }
    /**
     * It removes a domain from the list of domains.
     *
     * Override this function.
     *
     * @param id
     */
    removeByName(id) {
        this.domains = this.domains.filter(item => item.getName() != id);
    }
    /**
     * Help function which returns the list of domain string labels (map domain types).
     */
    getDomainNames() {
        const names = [];
        const domain = this.getDomains();
        if (domain != undefined) {
            for (let i = 0; i < domain.length; i++) {
                names.push(domain[i].getName());
            }
        }
        return names;
    }
    /**
     * The function returns map domains of given type.
     *
     * @param name
     */
    getDomain(name) {
        const domains = this.getDomains();
        if (domains != undefined) {
            for (let i = 0; i < domains.length; i++) {
                if (domains[i].getName() == name) {
                    return domains[i];
                }
            }
        }
        return undefined;
    }
}

/**
 * The class provides a basic implemention geographical data manager.
 *
 * @author Jiri Hynek
 */
class GeoDataManager extends MapDomainArrayManager {
    constructor(geoDataArray) {
        super(geoDataArray);
        /*--- Hierarchy interface ---*/
        this.hierarchyEnabledStatus = false;
        this.treesMap = new Map();
    }
    enableHierarchy(enabled) {
        this.hierarchyEnabledStatus = enabled;
    }
    isHierarchyEnabled() {
        return this.hierarchyEnabledStatus;
    }
    isHierarchyEnabledForDomain(domainName) {
        return this.treesMap.has(domainName);
    }
    getFeatures(name, types) {
        var _a, _b;
        const geoData = this.getDomain(name);
        const originalGeoData = geoData === null || geoData === void 0 ? void 0 : geoData.getFeatures(types);
        let active = [];
        const answer = {
            type: GeoJSONTypes$1.FeatureCollection,
            features: []
        };
        // Get active objects
        if (this.treesMap.has(name)) {
            active = (_b = (_a = this.treesMap.get(name)) === null || _a === void 0 ? void 0 : _a.getActive()) !== null && _b !== void 0 ? _b : [];
        }
        // Filter original geo-objects.
        originalGeoData === null || originalGeoData === void 0 ? void 0 : originalGeoData.features.forEach(feat => {
            if (feat.id) {
                if (active.includes(feat.id.toString())) {
                    answer.features.push(feat);
                }
            }
        });
        // If hierarchy is disabled, return original geo data
        if (!this.hierarchyEnabledStatus) {
            answer.features = [];
            originalGeoData === null || originalGeoData === void 0 ? void 0 : originalGeoData.features.forEach(feat => {
                answer.features.push(feat);
            });
        }
        return answer;
    }
    setTree(domainName, nodes, aggregationEnabled) {
        const newTree = new HierarchyTree(domainName);
        newTree.initByArray(nodes);
        newTree.setAggregationFlag(aggregationEnabled);
        this.treesMap.set(domainName, newTree);
    }
    startTree(domainName, zoom) {
        var _a;
        if (this.treesMap.has(domainName)) {
            (_a = this.treesMap.get(domainName)) === null || _a === void 0 ? void 0 : _a.startActiveWatch(zoom);
        }
    }
    updateTrees(zoom) {
        this.treesMap.forEach(tree => {
            tree.recalculate(zoom);
        });
    }
    treeAggregationFlag(domainName) {
        var _a, _b;
        if (this.treesMap.has(domainName)) {
            return (_b = (_a = this.treesMap.get(domainName)) === null || _a === void 0 ? void 0 : _a.getAggregationFlag()) !== null && _b !== void 0 ? _b : false;
        }
        return false;
    }
    getActiveByTree(domainName) {
        var _a, _b;
        let answer = [];
        if (this.treesMap.has(domainName)) {
            answer = (_b = (_a = this.treesMap.get(domainName)) === null || _a === void 0 ? void 0 : _a.getActive()) !== null && _b !== void 0 ? _b : [];
        }
        return answer;
    }
    getChildsFromTree(domainName, objectID) {
        var _a, _b;
        let answer = [];
        if (this.treesMap.has(domainName)) {
            answer = (_b = (_a = this.treesMap.get(domainName)) === null || _a === void 0 ? void 0 : _a.getAllLeafes(objectID)) !== null && _b !== void 0 ? _b : [];
        }
        return answer;
    }
}

/**
 * This class provides generic map event object.
 *
 * @author Jiri Hynek
 */
class MapEvent {
    /**
     * It initializes event.
     */
    constructor(type, source) {
        this.type = type;
        this.source = source;
    }
    /**
     * It returns string which identifies the event.
     */
    getType() {
        return this.type;
    }
    /**
     * It return source map object of the event.
     */
    getSource() {
        return this.source;
    }
}

/**
 * This class provides generic map change event object.
 *
 * @author Jiri Hynek
 */
class MapChangeEvent extends MapEvent {
    /**
     * It initializes event.
     */
    constructor(type, source, changedObject) {
        super(type, source);
        this.changedObject = changedObject;
    }
    /**
     * Return the changed object.
     */
    getChangedObject() {
        return this.changedObject;
    }
}

/**
 * This class provides the data change event object.
 *
 * @author Jiri Hynek
 */
class DataChangeEvent extends MapChangeEvent {
    /**
     * It initializes event.
     */
    constructor(source, data, animateOptions) {
        super(DataChangeEvent.TYPE(), source, data);
        this.animateOptions = animateOptions;
    }
    /**
     * Type of the event.
     */
    static TYPE() {
        return "data-change-event";
    }
    /**
     * It returns the animate options
     */
    getAnimateOptions() {
        return this.animateOptions;
    }
}

/**
 * This class provides the data change event object.
 *
 * @author Jiri Hynek
 */
class DataManagerChangeEvent extends MapChangeEvent {
    /**
     * It initializes event.
     */
    constructor(source, data) {
        super(DataManagerChangeEvent.TYPE(), source, data);
    }
    /**
     * Type of the event.
     */
    static TYPE() {
        return "data-manager-change-event";
    }
}

/**
 * This class provides the data change event object.
 *
 * @author Jiri Hynek
 */
class GeoDataManagerChangeEvent extends MapChangeEvent {
    /**
     * It initializes event.
     */
    constructor(source, data) {
        super(GeoDataManagerChangeEvent.TYPE(), source, data);
    }
    /**
     * Type of the event.
     */
    static TYPE() {
        return "geo-data-manager-change-event";
    }
}

var rfdc_1 = rfdc;

function copyBuffer (cur) {
  if (cur instanceof Buffer) {
    return Buffer.from(cur)
  }

  return new cur.constructor(cur.buffer.slice(), cur.byteOffset, cur.length)
}

function rfdc (opts) {
  opts = opts || {};

  if (opts.circles) return rfdcCircles(opts)
  return opts.proto ? cloneProto : clone

  function cloneArray (a, fn) {
    var keys = Object.keys(a);
    var a2 = new Array(keys.length);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      var cur = a[k];
      if (typeof cur !== 'object' || cur === null) {
        a2[k] = cur;
      } else if (cur instanceof Date) {
        a2[k] = new Date(cur);
      } else if (ArrayBuffer.isView(cur)) {
        a2[k] = copyBuffer(cur);
      } else {
        a2[k] = fn(cur);
      }
    }
    return a2
  }

  function clone (o) {
    if (typeof o !== 'object' || o === null) return o
    if (o instanceof Date) return new Date(o)
    if (Array.isArray(o)) return cloneArray(o, clone)
    if (o instanceof Map) return new Map(cloneArray(Array.from(o), clone))
    if (o instanceof Set) return new Set(cloneArray(Array.from(o), clone))
    var o2 = {};
    for (var k in o) {
      if (Object.hasOwnProperty.call(o, k) === false) continue
      var cur = o[k];
      if (typeof cur !== 'object' || cur === null) {
        o2[k] = cur;
      } else if (cur instanceof Date) {
        o2[k] = new Date(cur);
      } else if (cur instanceof Map) {
        o2[k] = new Map(cloneArray(Array.from(cur), clone));
      } else if (cur instanceof Set) {
        o2[k] = new Set(cloneArray(Array.from(cur), clone));
      } else if (ArrayBuffer.isView(cur)) {
        o2[k] = copyBuffer(cur);
      } else {
        o2[k] = clone(cur);
      }
    }
    return o2
  }

  function cloneProto (o) {
    if (typeof o !== 'object' || o === null) return o
    if (o instanceof Date) return new Date(o)
    if (Array.isArray(o)) return cloneArray(o, cloneProto)
    if (o instanceof Map) return new Map(cloneArray(Array.from(o), cloneProto))
    if (o instanceof Set) return new Set(cloneArray(Array.from(o), cloneProto))
    var o2 = {};
    for (var k in o) {
      var cur = o[k];
      if (typeof cur !== 'object' || cur === null) {
        o2[k] = cur;
      } else if (cur instanceof Date) {
        o2[k] = new Date(cur);
      } else if (cur instanceof Map) {
        o2[k] = new Map(cloneArray(Array.from(cur), cloneProto));
      } else if (cur instanceof Set) {
        o2[k] = new Set(cloneArray(Array.from(cur), cloneProto));
      } else if (ArrayBuffer.isView(cur)) {
        o2[k] = copyBuffer(cur);
      } else {
        o2[k] = cloneProto(cur);
      }
    }
    return o2
  }
}

function rfdcCircles (opts) {
  var refs = [];
  var refsNew = [];

  return opts.proto ? cloneProto : clone

  function cloneArray (a, fn) {
    var keys = Object.keys(a);
    var a2 = new Array(keys.length);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      var cur = a[k];
      if (typeof cur !== 'object' || cur === null) {
        a2[k] = cur;
      } else if (cur instanceof Date) {
        a2[k] = new Date(cur);
      } else if (ArrayBuffer.isView(cur)) {
        a2[k] = copyBuffer(cur);
      } else {
        var index = refs.indexOf(cur);
        if (index !== -1) {
          a2[k] = refsNew[index];
        } else {
          a2[k] = fn(cur);
        }
      }
    }
    return a2
  }

  function clone (o) {
    if (typeof o !== 'object' || o === null) return o
    if (o instanceof Date) return new Date(o)
    if (Array.isArray(o)) return cloneArray(o, clone)
    if (o instanceof Map) return new Map(cloneArray(Array.from(o), clone))
    if (o instanceof Set) return new Set(cloneArray(Array.from(o), clone))
    var o2 = {};
    refs.push(o);
    refsNew.push(o2);
    for (var k in o) {
      if (Object.hasOwnProperty.call(o, k) === false) continue
      var cur = o[k];
      if (typeof cur !== 'object' || cur === null) {
        o2[k] = cur;
      } else if (cur instanceof Date) {
        o2[k] = new Date(cur);
      } else if (cur instanceof Map) {
        o2[k] = new Map(cloneArray(Array.from(cur), clone));
      } else if (cur instanceof Set) {
        o2[k] = new Set(cloneArray(Array.from(cur), clone));
      } else if (ArrayBuffer.isView(cur)) {
        o2[k] = copyBuffer(cur);
      } else {
        var i = refs.indexOf(cur);
        if (i !== -1) {
          o2[k] = refsNew[i];
        } else {
          o2[k] = clone(cur);
        }
      }
    }
    refs.pop();
    refsNew.pop();
    return o2
  }

  function cloneProto (o) {
    if (typeof o !== 'object' || o === null) return o
    if (o instanceof Date) return new Date(o)
    if (Array.isArray(o)) return cloneArray(o, cloneProto)
    if (o instanceof Map) return new Map(cloneArray(Array.from(o), cloneProto))
    if (o instanceof Set) return new Set(cloneArray(Array.from(o), cloneProto))
    var o2 = {};
    refs.push(o);
    refsNew.push(o2);
    for (var k in o) {
      var cur = o[k];
      if (typeof cur !== 'object' || cur === null) {
        o2[k] = cur;
      } else if (cur instanceof Date) {
        o2[k] = new Date(cur);
      } else if (cur instanceof Map) {
        o2[k] = new Map(cloneArray(Array.from(cur), cloneProto));
      } else if (cur instanceof Set) {
        o2[k] = new Set(cloneArray(Array.from(cur), cloneProto));
      } else if (ArrayBuffer.isView(cur)) {
        o2[k] = copyBuffer(cur);
      } else {
        var i = refs.indexOf(cur);
        if (i !== -1) {
          o2[k] = refsNew[i];
        } else {
          o2[k] = cloneProto(cur);
        }
      }
    }
    refs.pop();
    refsNew.pop();
    return o2
  }
}

/**
 * The class wraps data used by the map, its metadata and functions to acquire data items.
 *
 * @author Jiri Hynek
 */
class AbstractMapDataManager {
    /**
     * It initializes the data wrapper providing a basic API.
     *
     * @param data
     */
    constructor(data) {
        this.data = data;
    }
    /**
     * It returns the original input data.
     */
    getOriginalData() {
        return this.data;
    }
    /**
     * Help function which returns the list of data domain string name.
     */
    getDomainNames() {
        const names = [];
        const dataDomains = this.getDomains();
        for (let i = 0; i < dataDomains.length; i++) {
            names.push(dataDomains[i].getName());
        }
        return names;
    }
    /**
     * It returns the data domain which corresponds to the given string
     * or creates a new one.
     *
     * @param name
     */
    getDomain(name) {
        const dataDomains = this.getDomains();
        if (dataDomains != undefined) {
            for (let i = 0; i < dataDomains.length; i++) {
                if (dataDomains[i].getName() == name) {
                    return dataDomains[i];
                }
            }
        }
        return undefined;
    }
}

/**
 * The class wraps meta data path used to find data.
 *
 * @author Jiri Hynek
 */
class AbstractMapDataDomain {
    /**
     *  It initializes the data domain wrapper providing a basic API.
     *
     * @param originalDataDomain
     */
    constructor(originalDataDomain) {
        this.originalDataDomain = originalDataDomain;
    }
    /**
     * The function returns the string representation of the map data domain
     * which is *unique* among the labels of other data domains.
     */
    getOriginal() {
        return this.originalDataDomain;
    }
}

/**
 * The class wraps meta data path used to find data.
 *
 * @author Jiri Hynek
 */
class JsonMapDataDomain extends AbstractMapDataDomain {
    constructor(domainDescription) {
        super(domainDescription);
        this.name = domainDescription.join().replace(/,/g, ".");
    }
    /**
     * The function returns the string representation of the map data domain
     * which is *unique* among the labels of other data domains.
     */
    getOriginal() {
        return super.getOriginal();
    }
    /**
     * The function returns the string representation of the map data domain
     * which is *unique* among the names of other data domains.
     *
     * It uses dots to delimiter the array items.
     */
    getName() {
        return this.name;
    }
}

/**
 * A data wrapper which provides a basic flattening of JSON data structure.
 *
 * @author Jiri Hynek
 */
class JsonMapDataManager extends AbstractMapDataManager {
    /**
     * It creates JSON data manager
     *
     * @param data
     */
    constructor(data) {
        super(data);
    }
    /**
     * It returns the original input data.
     */
    getOriginalData() {
        return super.getOriginalData();
    }
    /**
     * It returns preprocessed flattened data.
     */
    getDataRecords() {
        if (this.dataRecords == undefined) {
            this.dataRecords = this.createDataRecords(this.getOriginalData());
        }
        return this.dataRecords;
    }
    /**
     * It returns list of data domains (IMapDataDomain) representing data dimensions e. g.:
     * [
     *   [ 'value' ],
     *   [ 'source', 'ip' ],
     *   [ 'source', 'country' ],
     *   [ 'target', 'ip' ],
     *   [ 'target', 'country' ]
     * ]
     *
     */
    getDomains() {
        if (this.dataDomains == undefined) {
            this.dataDomains = this.createDataDomains();
        }
        return this.dataDomains;
    }
    /**
     * It returns the data domain which corresponds to the given string.
     *
     * If data domain does not exists it creates a new one (to avoid undefined return value)
     *
     * @param name
     */
    getDomain(name) {
        const dataDomain = super.getDomain(name);
        return dataDomain ? dataDomain : new JsonMapDataDomain(name.split('.'));
    }
    /**
     * It returns list of all values of the selected data domain.
     *
     * @param dataDomain
     */
    getValues(dataDomain) {
        return this.getDataRecordsValues(dataDomain, this.getDataRecords());
    }
    /**
     * It returns list of all values of the selected data domain
     * for the given subset of data.
     *
     * @param dataDomain
     * @param dataRecords
     */
    getDataRecordsValues(dataDomain, dataRecords) {
        const result = [];
        if (dataRecords != undefined) {
            for (let i = 0; i < dataRecords.length; i++) {
                const actResult = [];
                this.processDataDomainDescription(actResult, dataRecords[i], dataDomain.getOriginal(), 0);
                // add only unique results
                for (let j = 0; j < actResult.length; j++) {
                    if (!result.includes(actResult[j])) {
                        result.push(actResult[j]);
                    }
                }
            }
        }
        return result;
    }
    /**
     * It returns values stored of the selected data domain stored in the given data record.
     *
     * @param dataDomain
     * @param dataRecord
     */
    getDataRecordValues(dataDomain, dataRecord) {
        const result = [];
        if (dataRecord != undefined) {
            this.processDataDomainDescription(result, dataRecord, dataDomain.getOriginal(), 0);
        }
        return result;
    }
    /**
     * Help function which analyzes data and creates its metedata description.
     */
    createDataDomains() {
        /*
         * Tests if an array contains an item
         */
        const contains = function (dataDomains, dataDomain) {
            return dataDomains.some(domain => domain.getName() == dataDomain);
        };
        /*
         * *Recursive* build of data domains
         *
         * TODO: optimize
         */
        const processDataDomain = function (dataDomains, dataDomainValues, actValue) {
            if (typeof actValue == "object") {
                // object
                if (Array.isArray(actValue)) {
                    // array - in the case that the data are not flattened
                    // deprecated
                    dataDomainValues.push("[]");
                    for (let i = 0; i < actValue.length; i++) {
                        processDataDomain(dataDomains, dataDomainValues, actValue[i]);
                    }
                }
                else {
                    // structure (key, value)
                    const actKeys = Object.keys(actValue);
                    for (let j = 0; j < actKeys.length; j++) {
                        const dataDomainCopy = [...dataDomainValues];
                        dataDomainCopy.push(actKeys[j]);
                        processDataDomain(dataDomains, dataDomainCopy, actValue[actKeys[j]]);
                    }
                }
            }
            else {
                // simple value
                const newMapDataDomainString = dataDomainValues.join().replace(/,/g, ".");
                if (!contains(dataDomains, newMapDataDomainString)) {
                    const newMapDataDomain = new JsonMapDataDomain(dataDomainValues);
                    dataDomains.push(newMapDataDomain);
                }
            }
        };
        // process data -> builing list of data domains (simplified scheme)
        const dataDomains = [];
        let dataDomainValues = [];
        let actKeys;
        const data = this.getDataRecords(); // get flattened data
        for (let i = 0; i < data.length; i++) {
            actKeys = Object.keys(data[i]);
            for (let j = 0; j < actKeys.length; j++) {
                dataDomainValues = [actKeys[j]];
                processDataDomain(dataDomains, dataDomainValues, data[i][actKeys[j]]);
            }
        }
        // console.log("data domains:", dataDomains);
        return dataDomains;
    }
    /**
     * Help function which converts data to the flat structure.
     */
    createDataRecords(data) {
        /*
         * *Recursive* flattening of data
         *
         * TODO: optimize
         */
        const transformObject = function (actValue) {
            let result = null;
            const clone = rfdc_1();
            if (actValue != null && typeof actValue == "object") {
                // object
                if (Array.isArray(actValue)) {
                    // array
                    let transformedChildren = [];
                    let transformedChild;
                    for (let i = 0; i < actValue.length; i++) {
                        transformedChild = transformObject(actValue[i]);
                        if (Array.isArray(transformedChild)) {
                            transformedChildren = transformedChildren.concat(transformedChild);
                        }
                        else if (transformedChild != null) {
                            transformedChildren.push(transformedChild);
                        }
                    }
                    result = transformedChildren;
                }
                else {
                    // structure (key, value)
                    let transformedChild;
                    if (actValue != null) {
                        const actObject = actValue;
                        const actKeys = Object.keys(actObject);
                        result = [{}];
                        for (let i = 0; i < actKeys.length; i++) {
                            transformedChild = transformObject(actObject[actKeys[i]]);
                            if (Array.isArray(transformedChild)) {
                                // wee need to duplicate actual results
                                const newResults = [];
                                let copy;
                                for (let j = 0; j < result.length; j++) {
                                    for (let k = 0; k < transformedChild.length; k++) {
                                        copy = clone(result[j]);
                                        copy[actKeys[i]] = transformedChild[k];
                                        newResults.push(copy);
                                    }
                                }
                                result = newResults;
                            }
                            else if (transformedChild != null) {
                                for (let j = 0; j < result.length; j++) {
                                    result[j][actKeys[i]] = transformedChild;
                                }
                            }
                        }
                    }
                }
                // optimization
                if (result != null) {
                    if (result.length == 0) {
                        result = null;
                    }
                    else if (result.length == 1) {
                        // TODO ?
                        result = result[0];
                    }
                }
            }
            else {
                result = actValue;
            }
            return result;
        };
        const result = transformObject(data);
        const dataRecords = result != null ? result : [];
        // console.log("flattened data: ", dataRecords);
        return dataRecords;
    }
    /**
     * Static help function represents a step of recursive data processing searching data items.
     *
     * @param result
     * @param actRecord
     * @param dataDomain
     * @param i
     */
    processDataDomainDescription(result, actRecord, domainDescription, i) {
        if (actRecord != undefined && actRecord != null) {
            if (i == domainDescription.length) {
                // reached the value
                if (typeof actRecord == "string") {
                    result.push(actRecord);
                }
                else if (typeof actRecord != "object") {
                    result.push(actRecord);
                }
            }
            else {
                // act value needs to be type of object
                if (typeof actRecord == "object") {
                    const dataDomainPart = domainDescription[i];
                    // deprecated
                    if (dataDomainPart == "[]") {
                        // act value needs to be type of array
                        if (Array.isArray(actRecord)) {
                            for (let j = 0; j < actRecord.length; j++) {
                                this.processDataDomainDescription(result, actRecord[j], domainDescription, i + 1);
                            }
                        }
                    }
                    else {
                        // act value is structure
                        if (actRecord[dataDomainPart] != null) {
                            this.processDataDomainDescription(result, actRecord[dataDomainPart], domainDescription, i + 1);
                        }
                    }
                }
            }
        }
    }
}

/**
 * The class wraps config used by the map and functions to acquire config items.
 *
 * @author Jiri Hynek
 */
class AbstractMapConfigManager {
    /**
     * It initializes the config wrapper providing a basic API.
     * It expects a config represented by the implicict Geovisto map structure.
     *
     * @param config
     */
    constructor(config) {
        this.originalConfig = config;
        this.config = this.import(config);
    }
    /**
     * It returns the original config.
     */
    getOriginalConfig() {
        return this.originalConfig;
    }
    /**
     * It returns the map config
     */
    getMapConfig() {
        return this.config;
    }
    /**
     * It returns the list of all config records for the tools.
     */
    getToolsConfigs() {
        return this.config.tools;
    }
    /**
     * It returns the config record for the tool identified by the given tool identifier.
     *
     * @param toolId
     */
    getToolConfig(toolId) {
        const tools = this.config.tools;
        let toolConfig = undefined;
        if (tools != undefined && Array.isArray(tools)) {
            toolConfig = tools.find(x => x.id == toolId);
        }
        return toolConfig;
    }
}

/**
 * The class wraps config used by the map and functions to acquire config items.
 *
 * @author Jiri Hynek
 */
class MapConfigManager extends AbstractMapConfigManager {
    /**
     * It initializes the config wrapper providing a basic API.
     * It expects a config represented by the implicict Geovisto map structure.
     *
     * @param config
     */
    constructor(config) {
        super(config);
    }
    /**
     * It converts given config to the internal map config structure.
     * It keeps the config structure.
     *
     * @param mapConfing
     */
    import(config) {
        // we expect the external config in the config model
        // TODO: provide validation
        return config;
    }
    /**
     * It returns map config of the implicit structure.
     *
     * @param mapConfing
     */
    export(mapConfig) {
        return mapConfig;
    }
}

/**
 * This class defines functions for management and handling events and event listeners.
 *
 * @author Jiri Hynek
 */
class MapEventManager {
    /**
     * It creates a new map event manager.
     */
    constructor() {
        this.listeners = [];
        this.eventQueue = [];
    }
    /**
     * It schdules an event in the end of the event queue and dispatch the event.
     *
     * @param event
     */
    scheduleEvent(event, beforeCallBack, afterCallBack) {
        // echedule event to future
        this.eventQueue.push({ event: event, beforeCallBack: beforeCallBack, afterCallBack: afterCallBack });
        // if only this event is in queue, process the event
        if (this.eventQueue.length == 1) {
            // start event loop 
            // listeners might schedule further events
            let event;
            while (this.eventQueue.length > 0) {
                event = this.eventQueue[0];
                // handle event
                if (event.beforeCallBack) {
                    event.beforeCallBack();
                }
                this.dispatchEvent(event.event);
                if (event.afterCallBack) {
                    event.afterCallBack();
                }
                // remove the dispatched event
                this.eventQueue.shift();
            }
        }
    }
    /**
     * It sends custom event to all listeners (tools)
     *
     * @param event
     */
    dispatchEvent(event) {
        // console.log("event: " + event.getType(), event);
        for (let i = 0; i < this.listeners.length; i++) {
            this.listeners[i].handleEvent(event);
        }
    }
    /**
     * It adds an event listener.
     *
     * @param listener
     */
    addEventListener(listener) {
        if (!this.listeners.includes(listener)) {
            this.listeners.push(listener);
        }
    }
    /**
     * It removes an event listener.
     *
     * @param listener
     */
    removeEventListener(listener) {
        const index = this.listeners.indexOf(listener, 0);
        if (index > -1) {
            this.listeners.splice(index, 1);
        }
    }
}

/**
 * This class provide functions which return the default state values.
 *
 * @author Jiri Hynek
 */
class MapObjectDefaults {
    /**
     * It returns default props if no props are given.
     */
    getProps() {
        return {
            id: this.getId()
        };
    }
    /**
     * It returns a default config if no config is given.
     */
    getConfig() {
        return {
            type: undefined,
            id: undefined
        };
    }
    /**
     * It returns a unique type string of the tool.
     */
    getType() {
        return "geovisto-object-abstract";
    }
    /**
     * It returns identifier which is used when no identifier is specified.
     */
    getId() {
        if (!this.idString) {
            this.idString = this.generateId();
        }
        return this.idString;
    }
    /**
     * It returns identifier which is used when no identifier is specified.
     */
    generateId() {
        if (MapObjectDefaults.id == undefined) {
            MapObjectDefaults.id = 0;
        }
        MapObjectDefaults.id++;
        return this.getType() + "-" + MapObjectDefaults.id;
    }
}

/**
 * This class provide functions for using map objects which can be identified by uniquie string.
 *
 * @author Jiri Hynek
 */
class MapObjectsManager {
    /**
     * It initializes a map objects manager.
     */
    constructor(objects) {
        this.objects = objects ? objects : [];
    }
    /**
     * The function returns available map objects.
     */
    getAll() {
        return this.objects;
    }
    /**
     * The function returns number of objects.
     */
    size() {
        return this.objects.length;
    }
    /**
     * The function returns true if size() is 0.
     */
    isEmpty() {
        return this.objects.length == 0;
    }
    /**
     * It adds object to the list of objects.
     *
     * Override this function.
     *
     * @param object
     */
    add(object) {
        this.objects.push(object);
    }
    /**
     * It removes object from the list of objects.
     *
     * @param object
     */
    remove(object) {
        this.objects = this.objects.filter(item => item != object);
    }
    /**
     * It removes object from the list of objects.
     *
     * Override this function.
     *
     * @param id
     */
    removeById(id) {
        this.objects = this.objects.filter(item => item.getId() != id);
    }
    /**
     * Help function which returns the list of object string labels (object types).
     */
    getTypes() {
        const types = [];
        const objects = this.getAll();
        if (objects != undefined) {
            for (let i = 0; i < objects.length; i++) {
                types.push(objects[i].getType());
            }
        }
        return types;
    }
    /**
     * Help function which returns the list of object string labels (object types).
     */
    getIds() {
        const types = [];
        const objects = this.getAll();
        if (objects != undefined) {
            for (let i = 0; i < objects.length; i++) {
                types.push(objects[i].getId());
            }
        }
        return types;
    }
    /**
     * The function returns map objects of given type.
     *
     * @param type
     */
    getByType(type) {
        const objects = this.getAll();
        const resultObjects = [];
        if (objects != undefined) {
            for (let i = 0; i < objects.length; i++) {
                if (objects[i].getType() == type) {
                    resultObjects.push(objects[i]);
                }
            }
        }
        return resultObjects;
    }
    /**
     * The function returns map object of given unique identifier.
     *
     * @param id
     */
    getById(id) {
        const objects = this.getAll();
        if (objects != undefined) {
            for (let i = 0; i < objects.length; i++) {
                if (objects[i].getId() == id) {
                    return objects[i];
                }
            }
        }
        return undefined;
    }
}

/**
 * This class provide functions for using tools.
 *
 * @author Jiri Hynek
 */
class MapToolsManager extends MapObjectsManager {
    constructor(tools) {
        super(tools);
    }
    /**
     * It returns copy of the tools manager with copies of tools.
     */
    copy() {
        // we use copies of predefined tools due to later multiple imports of configs
        const toolsCopy = [];
        const tools = this.getAll();
        for (let i = 0; i < tools.length; i++) {
            // FIXME: this us a bug (deep copy of the object needs to be done, including all the overriden fields)
            toolsCopy.push(tools[i].copy());
        }
        return new MapToolsManager(toolsCopy);
    }
}

/**
 * This class provide functions which return the default state values.
 *
 * @author Jiri Hynek
 */
class GeovistoMapDefaults extends MapObjectDefaults {
    /**
     * It returns default map config manager.
     */
    getConfigManager() {
        return new MapConfigManager({});
    }
    /**
     * It returns default map config.
     *
     * All config variables are undefined since they might override the props.
     */
    getConfig() {
        return {
            id: undefined,
            type: undefined,
            tools: undefined,
            zoom: undefined,
            mapCenter: undefined,
            mapStructure: undefined
        };
    }
    /**
     * It returns a unique type string of the object.
     */
    getType() {
        return GeovistoMapDefaults.TYPE;
    }
    /**
     * It returns a default map event manager.
     *
     * @returns event manager
     */
    getEventManager() {
        return new MapEventManager();
    }
    /**
     * It returns a default managers providing templates.
     */
    getTemplates() {
        return {
            tools: this.getToolTemplates(),
        };
    }
    /**
     * It returns a default tools manager containing used tools.
     */
    getToolTemplates() {
        return new MapToolsManager([]);
    }
    /**
     * It returns a default tools manager containing used tools.
     */
    getTools() {
        return new MapToolsManager([]);
    }
    /**
     * It returns a default map data manager.
     */
    getMapData() {
        return new JsonMapDataManager([]);
    }
    /**
     * It returns a default geo data manager.
     */
    getGeoDataManager() {
        return new GeoDataManager([
        // TODO: provide simple default GeoJSON
        ]);
    }
    /**
     * It returns a default global state variables.
     */
    getGlobals() {
        return {
            zoom: this.getZoom(),
            mapCenter: this.getMapCenter(),
            mapStructure: this.getMapStructure(),
        };
    }
    /**
     * It returns a default zoom level.
     */
    getZoom() {
        return 2;
    }
    /**
     * It returns a default center coordinates in Leaflet map.
     */
    getMapCenter() {
        return {
            lat: 50,
            lng: -0.1
        };
    }
    /**
     * It returns a default map structure defined with respect to the leaflet library.
     */
    getMapStructure() {
        return {
            maxZoom: 20,
            maxBounds: [[-100, -400], [2000, 400]]
        };
    }
}
GeovistoMapDefaults.TYPE = "geovisto-map";

/**
 * This class manages state of the tool.
 * It wraps the state since the tool can work with state objects which needs to be explicitly serialized.
 *
 * @author Jiri Hynek
 */
class AbstractMapObjectState {
    /**
     * It creates a map object state.
     */
    constructor(mapObject) {
        this.mapObject = mapObject;
        const props = mapObject.getProps();
        const defaults = mapObject.getDefaults();
        // sets the type of the object (can be set only once in the constructor)
        this.type = defaults.getType();
        // set the id of the object (can be set only once in the constructor)
        this.id = props.id == undefined ? defaults.getId() : props.id;
    }
    /**
     * It makes the map object visible to extended classes.
     */
    getMapObject() {
        return this.mapObject;
    }
    /**
     * It resets the state to the initial props.
     *
     * @param defaults
     * @param props
     * @param initProps
     */
    initialize(defaults, props, initProps) {
        // deserialize config which overrides the defined state props if defined
        this.deserialize(initProps.config == undefined ? defaults.getConfig() : initProps.config);
    }
    /**
     * The metod takes config and deserializes the values.
     *
     * @param config
     */
    deserialize(config) {
        if (config.id != undefined)
            this.setId(config.id);
    }
    /**
     * The method serializes the tool state. Optionally, a serialized value can be let undefined if it equals the default value.
     *
     * @param defaults
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    serialize(defaults) {
        return {
            type: this.getType(),
            id: this.getId(),
        };
    }
    /**
     * It returns the type property of the tool state.
     */
    getType() {
        return this.type;
    }
    /**
     * It returns the id property of the tool state.
     */
    getId() {
        return this.id;
    }
    /**
     * It sets the id property of the tool state.
     *
     * @param id
     */
    setId(id) {
        this.id = id;
    }
}

/**
 * This class manages state of the map.
 * It wraps the state since the map can work with state objects which needs to be explicitly serialized.
 *
 * @author Jiri Hynek
 */
class GeovistoMapState extends AbstractMapObjectState {
    /**
     * It initializes a map state.
     *
     * @param map
     */
    constructor(map) {
        super(map);
        this.eventManager = map.getDefaults().getEventManager();
        this.toolsAPI = {};
    }
    /**
     * It resets the state to the initial props.
     *
     * @param defaults
     * @param props
     * @param initProps
     */
    initialize(defaults, props, initProps) {
        // templates
        const templates = props.templates == undefined ? defaults.getTemplates() : props.templates;
        this.setToolTemplates(templates.tools == undefined ? defaults.getToolTemplates() : templates.tools);
        // tools
        this.setTools(props.tools == undefined ? defaults.getTools() : props.tools);
        // data
        this.setMapData(props.data == undefined ? defaults.getMapData() : props.data);
        // geo data
        this.setGeoDataManager(props.geoData == undefined ? defaults.getGeoDataManager() : props.geoData);
        // globals (state variables which are common for all geovisto tools) - can be undefined and set by initialize function
        const globals = props.globals == undefined ? defaults.getGlobals() : props.globals;
        this.setInitialZoom(globals.zoom == undefined ? defaults.getZoom() : globals.zoom);
        this.setInitialMapCenter(globals.mapCenter == undefined ? defaults.getMapCenter() : globals.mapCenter);
        this.setInitialMapStructure(globals.mapStructure == undefined ? defaults.getMapStructure() : globals.mapStructure);
        // set map config manager
        this.setMapConfig(initProps.configManager);
        // set super props
        super.initialize(defaults, props, initProps);
    }
    /**
     * It takes config and deserializes the values.
     *
     * @param config
     */
    deserialize(config) {
        super.deserialize(config);
        if (config.zoom != undefined)
            this.setInitialZoom(config.zoom);
        if (config.mapCenter != undefined)
            this.setInitialMapCenter(config.mapCenter);
        if (config.mapStructure != undefined)
            this.setInitialMapStructure(config.mapStructure);
    }
    /**
     * It serializes the map state. Optionally, a serialized value can be let undefined if it equals the default value.
     *
     * @param defaults
     */
    serialize(defaults) {
        const leafletMap = this.getLeafletMap();
        // initialize config
        // do not serialize the id and type for map
        //let config = super.serialize(defaults);
        const config = {
            id: undefined,
            type: undefined,
            zoom: defaults && (leafletMap === null || leafletMap === void 0 ? void 0 : leafletMap.getZoom()) == defaults.getZoom() ? undefined : leafletMap === null || leafletMap === void 0 ? void 0 : leafletMap.getZoom(),
            mapCenter: defaults && (leafletMap === null || leafletMap === void 0 ? void 0 : leafletMap.getCenter()) == defaults.getMapCenter() ? undefined : leafletMap === null || leafletMap === void 0 ? void 0 : leafletMap.getCenter(),
            mapStructure: undefined,
            tools: undefined // see the code below
        };
        // serialize tools
        const tools = this.getTools().getAll();
        const toolsConfigs = [];
        for (let i = 0; i < tools.length; i++) {
            toolsConfigs.push(tools[i].getState().serialize(defaults ? tools[i].getDefaults() : undefined));
        }
        config.tools = toolsConfigs;
        return config;
    }
    /**
     * It returns the map event manager.
     */
    getEventManager() {
        return this.eventManager;
    }
    /**
     * It returns the map event manager.
     *
     * @param eventManager
     */
    setEventManager(eventManager) {
        this.eventManager = eventManager;
    }
    /**
     * It returns the map tools API.
     */
    getToolsAPI() {
        return this.toolsAPI;
    }
    /**
     * It returns the Leaflet map.
     */
    getLeafletMap() {
        return this.leafletMap;
    }
    /**
     * It returns the Leaflet map.
     *
     * @param map
     */
    setLeafletMap(map) {
        this.leafletMap = map;
    }
    /**
     * It returns the tool manager providing tool templates.
     */
    getToolTemplates() {
        return this.toolTemplates;
    }
    /**
     * It sets tool templates providing tool templates.
     *
     * @param toolTemplates
     */
    setToolTemplates(toolTemplates) {
        this.toolTemplates = toolTemplates;
    }
    /**
     * It returns tools manager providing tools.
     */
    getTools() {
        return this.tools;
    }
    /**
     * It sets tools manager providing tools.
     *
     * @param tools
     */
    setTools(tools) {
        // we use copies of predefined tools due to later multiple imports of configs
        this.tools = tools.copy();
    }
    /**
     * It returns the map data manager.
     */
    getMapData() {
        return this.mapData;
    }
    /**
     * It sets the map data manager.
     * note: It also updates the current data.
     *
     * @param mapData
     */
    setMapData(mapData) {
        this.mapData = mapData;
        this.setCurrentData(mapData.getDataRecords());
    }
    /**
     * It returns current data (might be filtered).
     */
    getCurrentData() {
        return this.data;
    }
    /**
     * It sets current data.
     *
     * @param data
     */
    setCurrentData(data) {
        this.data = data;
    }
    /**
     * It returns the map config manager.
     */
    getMapConfig() {
        return this.mapConfig;
    }
    /**
     * It sets the map config manager.
     *
     * @param mapData
     */
    setMapConfig(mapConfig) {
        this.mapConfig = mapConfig;
    }
    /**
     * It returns the geo data manager.
     */
    getGeoDataManager() {
        return this.geoDataManager;
    }
    /**
     * It sets a geo data manager.
     *
     * @param geoDataManager
     */
    setGeoDataManager(geoDataManager) {
        this.geoDataManager = geoDataManager;
    }
    /**
     * It returns the initial zoom level.
     */
    getInitialZoom() {
        return this.zoom;
    }
    /**
     * It sets initial zoom level.
     *
     * @param zoom
     */
    setInitialZoom(zoom) {
        this.zoom = zoom;
    }
    /**
     * It returns the initial map center.
     *
     * TODO: remove from state (use defaults only)
     */
    getInitialMapCenter() {
        return this.mapCenter;
    }
    /**
     * It sets initial map center.
     *
     * TODO: remove from state (use defaults only)
     *
     * @param mapCenter
     */
    setInitialMapCenter(mapCenter) {
        this.mapCenter = mapCenter;
    }
    /**
     * It returns the initial structure.
     *
     * TODO: remove from state (use defaults only)
     */
    getInitialMapStructure() {
        return this.mapStructure;
    }
    /**
     * It sets initial map structure.
     *
     * TODO: remove from state (use defaults only)
     *
     * @param mapStructure
     */
    setInitialMapStructure(mapStructure) {
        this.mapStructure = mapStructure;
    }
}

/**
 * This class provide functions for using map object which can be identified by uniquie string.
 *
 * @author Jiri Hynek
 */
class MapObject {
    /**
     * It creates a map object.
     */
    constructor(props) {
        // create defaults of the object
        this.defaults = this.createDefaults();
        // get default props if undefined
        this.props = props == undefined ? this.defaults.getProps() : props;
        // create state of the object
        this.state = this.createState();
        //console.log("new object:", this.defaults.getType());
    }
    /**
     * It updates the props.
     */
    setProps(props) {
        this.props = props;
    }
    /**
     * It returns the props given by the programmer.
     */
    getProps() {
        return this.props;
    }
    /**
     * It returns default values of the state properties.
     */
    getDefaults() {
        return this.defaults;
    }
    /**
     * It creates new defaults of the object.
     *
     * This function can be overriden.
     */
    createDefaults() {
        return new MapObjectDefaults();
    }
    /**
     * It returns the tool state.
     *
     * This function should not be overriden.
     */
    getState() {
        return this.state;
    }
    /**
     * It creates new state if the object.
     *
     * This function can be overriden.
     */
    createState() {
        return new AbstractMapObjectState(this);
    }
    /**
     * Help function which returns the type of the object.
     */
    getType() {
        return this.state.getType();
    }
    /**
     * Help function which returns the id of the object.
     */
    getId() {
        return this.state.getId();
    }
    /**
     * It initializes the state of the object.
     * It processes the serialized config and sets further objects.
     *
     * This cannot be done in the object constructor
     * since the object can be created before the Geovisto map is created.
     *
     * @param initProps
     */
    initialize(initProps) {
        // override state by the config if specified in argument
        this.getState().initialize(this.getDefaults(), this.getProps(), initProps);
        return this;
    }
}

/**
 * Representation of map wrapper which handles map layers, sidebar and other tools
 *
 * @author Jiri Hynek
 */
class GeovistoMap extends MapObject {
    /**
     * Initializes object.
     *
     * @param props
     */
    constructor(props) {
        super(props);
    }
    /**
     * Help function which returns the props given by the programmer.
     */
    getProps() {
        return super.getProps();
    }
    /**
     * It returns object defaults as the map defaults.
     */
    getDefaults() {
        return super.getDefaults();
    }
    /**
     * It creates new defaults of the object.
     */
    createDefaults() {
        return new GeovistoMapDefaults();
    }
    /**
     * It returns object state as the map state.
     */
    getState() {
        return super.getState();
    }
    /**
     * It creates new state if the object.
     */
    createState() {
        return new GeovistoMapState(this);
    }
    /**
     * The function draws a new map.
     */
    draw(configManager) {
        // initialize map and tools
        this.initialize({ config: configManager.getMapConfig(), configManager: configManager });
        // render map and tools
        return this.create();
    }
    /**
     * This function redraws the current map.
     */
    redraw(configManager, props) {
        // get map and remove map children
        let mapContainer = document.getElementById(this.getState().getId());
        if (mapContainer && mapContainer.children.length > 0) {
            // remove old elements
            mapContainer.children[0].remove();
            if (props) {
                this.setProps(props);
            }
            // initialize map and tools
            this.initialize({ config: configManager.getMapConfig(), configManager: configManager });
            // render map and tools
            mapContainer = this.create();
        }
        return mapContainer;
    }
    /**
     * It resets the state to the initial state.
     *
     * @param initProps
     */
    initialize(initProps) {
        // init the map state
        this.getState().initialize(this.getDefaults(), this.getProps(), initProps);
        // initialize existing tools
        const toolsManager = this.getState().getTools();
        if (!toolsManager.isEmpty()) {
            // a) tool is already created, initialize them and try to find their config
            const tools = toolsManager.getAll();
            for (let i = 0; i < tools.length; i++) {
                // initialize tool (provide map and config)
                tools[i].initialize({ config: initProps.configManager.getToolConfig(tools[i].getId()), map: this });
                // register API if available
                this.registerToolAPI(tools[i]);
            }
        }
        // deserialize remaining tools with respect to the config
        const toolsConfigs = initProps.configManager.getToolsConfigs();
        if (toolsConfigs != undefined) {
            const toolTemplatesManager = this.getState().getToolTemplates();
            let tool;
            let toolConfig;
            for (let i = 0; i < toolsConfigs.length; i++) {
                toolConfig = toolsConfigs[i];
                // filter already initialized tools
                if (toolConfig.id) {
                    tool = toolsManager.getById(toolConfig.id);
                    if (tool) {
                        continue;
                    }
                }
                // b) tool has not been created yet, use config and tool template to create the tool
                if (toolConfig.type) {
                    const toolTemplates = toolTemplatesManager.getByType(toolConfig.type);
                    if (toolTemplates.length > 0) {
                        // filter singleton duplicates
                        if (toolTemplates[0].isSingleton() && toolsManager.getByType(toolConfig.type).length > 0) {
                            continue;
                        }
                        // create copy of the tool template
                        tool = toolTemplates[0].copy();
                        // initialize tool
                        tool.initialize({ config: toolConfig, map: this });
                        // register API if available
                        this.registerToolAPI(tool);
                        // add to the list of tools
                        toolsManager.add(tool);
                    }
                }
                // if there is no template, the tool config is ignored
            }
        }
        return this;
    }
    /**
     * Help function which register a generic tool API.
     *
     * @param tool
     */
    registerToolAPI(tool) {
        const apiGetter = tool.getAPIGetter();
        if (apiGetter) {
            const api = this.getState().getToolsAPI();
            for (const method in apiGetter) {
                if (!api[method]) {
                    api[method] = apiGetter[method];
                }
            }
        }
        return null;
    }
    /**
     * It exports the serialized representation of the current state of the map.
     */
    export() {
        return this.getState().getMapConfig().export(this.getState().serialize(this.getDefaults()));
    }
    /**
     * This function creates Geovisto map - it creates map container, leaflet map and tools.
     */
    create() {
        const mapContainer = this.createMapContainer();
        // create new map container (DOM element)
        if (mapContainer) {
            // create Leaflet map
            if (this.createMap()) {
                // create tools
                this.createTools();
            }
        }
        return mapContainer;
    }
    /**
     * This function creates the map container.
     */
    createMapContainer() {
        const mapContainer = document.getElementById(this.getState().getId());
        if (mapContainer) {
            const mapElement = mapContainer.appendChild(document.createElement("div"));
            mapElement.setAttribute("id", this.getContainerId());
            mapElement.setAttribute("class", this.getContainerClass());
        }
        return mapContainer;
    }
    /**
     * It returns ID of the map container.
     */
    getContainerId() {
        return this.getId() + "-container";
    }
    /**
     * It returns class of the map container.
     */
    getContainerClass() {
        return this.getType() + "-container";
    }
    /**
     * Creates the leaflet-based map with respect to the configuration.
     */
    createMap() {
        const state = this.getState();
        const map = L
            .map(this.getContainerId(), Object.assign({}, state.getInitialMapStructure()))
            .setView(state.getInitialMapCenter(), state.getInitialZoom());
        // add attribution
        map.attributionControl.addAttribution(this.getMapAttribution());
        this.getState().setLeafletMap(map);
        return map;
    }
    /**
     * It returns the map attribution.
     *
     * This function can be overriden;
     */
    getMapAttribution() {
        return 'Geovisto | VUT FIT Brno';
    }
    /**
     * This function creates map tools.
     */
    createTools() {
        // create tools
        const tools = this.getState().getTools().getAll();
        for (let i = 0; i < tools.length; i++) {
            // create tool
            tools[i].create();
        }
        return tools;
    }
    /**
     * It updates data and notifies listeners.
     *
     * @param dataManager
     */
    updateData(dataManager) {
        // create and dispatch event
        this.getState().getEventManager().scheduleEvent(new DataManagerChangeEvent(this, dataManager), () => {
            this.getState().setMapData(dataManager);
        }, // update data in map state (it updates the current data as well)
        undefined);
    }
    /**
     * It updates geo data and notifies listeners.
     *
     * @param geoDataManager
     */
    updateGeoData(geoDataManager) {
        // create and dispatch event
        this.getState().getEventManager().scheduleEvent(new GeoDataManagerChangeEvent(this, geoDataManager), () => {
            this.getState().setGeoDataManager(geoDataManager);
        }, // update geo data manager in map state
        undefined);
    }
    /**
     * It updates current data and invokes notifies listeners.
     *
     * @param data
     * @param source of the change
     * @param animateOptions
     */
    updateCurrentData(data, source, animateOptions) {
        // create and dispatch event
        this.getState().getEventManager().scheduleEvent(new DataChangeEvent(source, data, animateOptions), () => { this.getState().setCurrentData(data); }, // update current data in map state
        undefined);
    }
}

/**
 * This class provides a factory for config managers.
 *
 * @author Jiri Hynek
 */
class MapConfigManagerFactory {
    /**
     * It creates the default config manager function.
     */
    default(config) {
        return new MapConfigManager(config);
    }
}

/**
 * This class provides a factory for map data managers.
 *
 * @author Jiri Hynek
 */
class MapAggregationFunctionFactory$1 {
    /**
     * It creates the JSON map data mananger.
     */
    json(data) {
        return new JsonMapDataManager(data);
    }
}

/**
 * The class wraps a map type dimension and its properties.
 *
 * @author Jiri Hynek
 */
class AbstractMapDimension extends AbstractMapDomain {
    /**
     * It creates a new map dimension.
     *
     * @param name
     * @param typeManager
     * @param value
     */
    constructor(name, value) {
        super();
        this.name = name;
        this.value = value;
    }
    /**
     * It returns the name of the dimension
     */
    getName() {
        return this.name;
    }
    /**
     * It sets the name of the dimension
     */
    setName(name) {
        this.name = name;
    }
    /**
     * It returns the value which is set to the map dimension.
     */
    getValue() {
        return this.value;
    }
    /**
     * It sets a new value to the map dimension.
     *
     * @param value
     */
    setValue(value) {
        this.value = value;
    }
    /**
     * It deserializes the string representation of a given value.
     *
     * @param value
     */
    setStringValue(value) {
        this.value = this.findValue(value);
    }
}

/**
 * The class wraps a map domain dimension and its properties.
 *
 * @author Jiri Hynek
 */
class MapDomainDimension$1 extends AbstractMapDimension {
    /**
     * It creates a new map dimension.
     *
     * @param name
     * @param domainManager
     * @param dataDomain
     */
    constructor(name, domainManager, dataDomain) {
        super(name, dataDomain);
        this.domainManager = domainManager;
    }
    /**
     * It returns the map domain manager which is set to the map dimension.
     */
    getDomainManager() {
        return this.domainManager;
    }
    /**
     * It sets a map domain manager which is set to the map dimension.
     *
     * @param domainManager
     */
    setDomainManager(domainManager) {
        this.domainManager = domainManager;
    }
    /**
     * It finds the value of given string.
     *
     * @param domainName
     */
    findValue(domainName) {
        return this.getDomainManager().getDomain(domainName);
    }
}

/**
 * This class provides a factory for map domain managers.
 *
 * @author Jiri Hynek
 */
class MapAggregationFunctionFactory {
    /**
     * It creates the map arary domain mananger.
     */
    array(domains) {
        return new MapDomainArrayManager(domains);
    }
}

/**
 * The class wraps a map dynamin domain dimension and its properties.
 *
 * @author Jiri Hynek
 */
class MapDynamicDomainDimension extends AbstractMapDimension {
    /**
     * It creates a new map dimension.
     *
     * @param name
     * @param dataDomain
     */
    constructor(name, domainManagerLoader, domainName) {
        super(name, undefined);
        this.domainManagerLoader = domainManagerLoader;
        this.domainName = domainName;
    }
    /**
     * It returns the map domain manager which is set to the map dimension.
     */
    getDomainManager() {
        return this.domainManagerLoader();
    }
    /**
     * It sets a map domain manager which is set to the map dimension.
     *
     * @param domainManager
     */
    setDomainManager() {
        return;
    }
    /**
     * It returns the map domain manager loader which is set to the map dimension.
     */
    getDomainManagerLoader() {
        return this.domainManagerLoader;
    }
    /**
     * It sets a map domain manager loader which is set to the map dimension.
     *
     * @param domainManagerLoader
     */
    setDomainManagerLoader(domainManagerLoader) {
        this.domainManagerLoader = domainManagerLoader;
    }
    /**
     * It returns the map domain which is set to the map dimension.
     */
    getValue() {
        const value = super.getValue();
        if (value === undefined) {
            return this.getDomainManager().getDomain(this.domainName);
        }
        return value;
    }
    /**
     * It looks for the map domain of the given name and sets it the map dimension.
     *
     * @param domainName
     */
    setStringValue(domainName) {
        this.domainName = domainName;
        super.setValue(undefined);
    }
    /**
     * It finds the value of given string.
     *
     * @param domainName
     */
    findValue(domainName) {
        return this.getDomainManager().getDomain(domainName);
    }
}

/**
 * This class provides a factory for map events.
 *
 * @author Jiri Hynek
 */
class MapEventFactory {
    /**
     * It creates a generic event.
     */
    default(type, source) {
        return new MapEvent(type, source);
    }
    /**
     * It creates a generic change event.
     */
    change(type, source, changedObject) {
        return new MapChangeEvent(type, source, changedObject);
    }
    /**
     * It creates the data change event.
     */
    dataChange(source, data) {
        return new DataChangeEvent(source, data);
    }
}

/**
 * The class wraps a map type dimension and its properties.
 *
 * @author Jiri Hynek
 */
class MapDomainDimension extends AbstractMapDimension {
    /**
     * It creates a new map dimension.
     *
     * @param name
     * @param typeManager
     * @param value
     */
    constructor(name, typeManager, value) {
        super(name, value);
        this.typeManager = typeManager;
    }
    /**
     * It returns the type constraint.
     */
    getTypeManager() {
        return this.typeManager;
    }
    /**
     * It sets a type constraint.
     *
     * @param typeConstraint
     */
    setTypeManager(typeManager) {
        this.typeManager = typeManager;
    }
    /**
     * It finds the value of given string.
     *
     * @param value
     */
    findValue(value) {
        return this.getTypeManager().deserialize(value);
    }
}

/**
 * This class specifies the empty type constraint.
 *
 * @author Jiri Hynek
 */
class StringTypeManager {
    /**
     * It keeps the string representation.
     */
    deserialize(value) {
        return value;
    }
}

const Geovisto = {
    getType: () => GeovistoMapDefaults.TYPE,
    createMap: (props) => new GeovistoMap(props),
    getMapAggregationFunctionFactory: () => new MapAggregationFunctionFactory$2(),
    getMapConfigManagerFactory: () => new MapConfigManagerFactory(),
    getMapDataManagerFactory: () => new MapAggregationFunctionFactory$1(),
    getMapDomainManagerFactory: () => new MapAggregationFunctionFactory(),
    getMapEventFactory: () => new MapEventFactory(),
    getGeoDataManager: (geoDataArray) => new GeoDataManager(geoDataArray),
    getGeoDataFactory: () => new GeoDataFactory(),
    getBooleanTypeManager: () => new BooleanTypeManager,
    getIntegerRangeManager: (min, max) => new IntegerRangeManager(min, max),
    getIntegerTypeManager: () => new IntegerTypeManager(),
    getStringTypeManager: () => new StringTypeManager(),
    createMapDomainDimension: (name, domainManager, dataDomain) => new MapDomainDimension$1(name, domainManager, dataDomain),
    createMapDynamicDomainDimension: (name, domainManagerLoader, domainName) => new MapDynamicDomainDimension(name, domainManagerLoader, domainName),
    createMapTypeDimension: (name, typeManager, value) => new MapDomainDimension(name, typeManager, value),
    createMapObjectsManager: (objects) => new MapObjectsManager(objects),
    createMapToolsManager: (tools) => new MapToolsManager(tools),
};

/**
 * This class provides event, dispatched in case of geo data change.
 *
 * @author Jiri Hynek
 */
class GeoDataChangeEvent extends MapChangeEvent {
    constructor(tool) {
        super(GeoDataChangeEvent.TYPE(), tool, null);
    }
    static TYPE() {
        return "geo-data-change-event";
    }
}

/**
 * This class provides the layer tool dimension change event object.
 *
 * @author Tomas Koscielniak
 * @author Jiri Hynek
 */
class LayerToolDimensionChangeEvent extends MapEvent {
    /**
     * It initializes event.
     */
    constructor(source) {
        super(LayerToolDimensionChangeEvent.TYPE(), source);
    }
    /**
     * Type of the event.
     */
    static TYPE() {
        return "dimension-change-event";
    }
}

/**
 * This class provides the layer tool rendered event.
 *
 * @author Jiri Hynek
 */
class LayerToolRenderedEvent extends MapChangeEvent {
    /**
     * It initializes event.
     */
    constructor(source, renderType) {
        super(LayerToolRenderedEvent.TYPE(), source, renderType);
    }
    /**
     * Type of the event.
     */
    static TYPE() {
        return "layer-tool-rendered-event";
    }
}

/**
 * This class provides the tool enabled event.
 *
 * @author Jiri Hynek
 */
class ToolEnabledEvent extends MapChangeEvent {
    /**
     * It initializes event.
     */
    constructor(source, enabled) {
        super(ToolEnabledEvent.TYPE(), source, enabled);
    }
    /**
     * Type of the event.
     */
    static TYPE() {
        return "tool-enabled-event";
    }
}

/**
 * This class represents an abstract form input.
 *
 * @author Jiri Hynek
 */
class AbstractMapFormInput {
    constructor(props) {
        this.props = props;
    }
    /**
     * It provides the props to the exteded classes.
     */
    getProps() {
        return this.props;
    }
}

/**
 * Class wraps help static function which generates DOM nodes.
 *
 * @author Jiri Hynek
 */
class TabDOMUtil {
    /**
     * Function generates select div node.
     *
     * @param label
     * @param id
     * @param action
     * @param options
     */
    static createSelectDiv(label, id, action, options) {
        const div = document.createElement('div');
        if (label != null) {
            div.appendChild(document.createTextNode(label + ": "));
        }
        div.appendChild(TabDOMUtil.createSelect(id, action, options, undefined));
        return div;
    }
    /**
     * Function generates select node.
     *
     * @param id
     * @param action
     * @param options
     * @param className
     */
    static createSelect(id, action, options, className) {
        const select = document.createElement('select');
        select.setAttribute("id", id);
        if (className) {
            select.classList.add(className);
        }
        select.onchange = action;
        TabDOMUtil.appendOptions(select, options);
        return select;
    }
    /**
     * Function genearates option nodes.
     *
     * @param select
     * @param options
     */
    static appendOptions(select, options) {
        let option;
        for (let i = 0; i < options.length; i++) {
            option = select.appendChild(document.createElement("option"));
            option.setAttribute("value", options[i]);
            option.innerHTML = options[i];
        }
    }
    /**
     * Function generates input text node.
     *
     * @param size
     * @param className
     */
    static createTextInput(size, className) {
        const input = document.createElement('input');
        input.setAttribute("type", "text");
        if (size) {
            input.setAttribute("size", size);
        }
        if (className) {
            input.classList.add(className);
        }
        return input;
    }
    /**
     * Function generates button node.
     *
     * @param label
     * @param action
     * @param id
     */
    static createButton(label, action, id) {
        const btn = document.createElement('button');
        btn.setAttribute("type", "button");
        btn.setAttribute("id", id);
        btn.innerHTML = label;
        btn.onclick = action;
        return btn;
    }
    /**
     * Function generates label heading node.
     *
     * @param label
     */
    static createSidebarHeading(label) {
        const heading = document.createElement('strong');
        heading.innerHTML = label;
        return heading;
    }
    /**
     * Sets multiple element attributes.
     *
     * @param element
     * @param keyArray
     * @param valueArray
     */
    static setAttributes(element, keyArray, valueArray) {
        if (keyArray.length == valueArray.length) {
            for (let i = keyArray.length - 1; i >= 0; i--) {
                element.setAttribute(keyArray[i], valueArray[i]);
            }
        }
    }
}

const ID$a = "geovisto-input-autocomplete";
const COMPONENT_DIV_LABEL_CLASS$2 = ID$a + "-label";
const COMPONENT_DIV_INPUT_CLASS$2 = ID$a + "-component";
const COMPONENT_INPUT_CLASS$1 = ID$a + "-value";
const COMPONENT_COMPLETION_LIST_CLASS = ID$a + "-popup";
const COMPONENT_COMPLETION_ITEM_CLASS = ID$a + "-option-m";
const COMPONENT_COMPLETION_NOT_IN_CLASS = ID$a + "-option-n";
const COMPONENT_COMPLETION_ACTIVE_ITEM_CLASS = ID$a + "-option-a";
/**
 * This class represents labeled text form input with autocomplete.
 *
 * @author Jakub Kachlik
 * @author Jiri Hynek (refactoring, code review)
 */
class LabeledAutocompleteFormInput extends AbstractMapFormInput {
    constructor(props) {
        super(props);
        this.options = props.options ? props.options : [];
        this.placeholder = props.placeholder || "";
        this.setData = props.setData;
        // index of selected item in the completion list
        this.selectedCompletionItem = -1;
    }
    /**
     * Static function returns identifier of the input type.
     */
    static ID() {
        return ID$a;
    }
    /*
     * Sets/removes attribute 'disabled' from input box.
     */
    setDisabled(disabled) {
        if (this.input) {
            if (disabled == true) {
                this.input.setAttribute("disabled", "true");
            }
            else {
                this.input.removeAttribute("disabled");
            }
        }
    }
    /*
     * Changes options for the completion list.
     */
    changeOptions(newOptions) {
        this.options = newOptions;
        this.redrawMenu();
    }
    redrawMenu() {
        this.createMenu();
    }
    /*
     * Changes options for the completion list.
     */
    setOptions(newOptions) {
        this.options = newOptions;
    }
    /*
     * Divides options into the two groups (matched and not matched values) with respect to the currently selected input.
     */
    getCurrentOptions() {
        const matchArray = [];
        const notMatchArray = [];
        if (this.input) {
            for (let i = 0; i < this.options.length; i++) {
                if (new String(this.options[i]).includes(this.input.value)) {
                    matchArray.push(this.options[i]);
                }
                else {
                    notMatchArray.push(this.options[i]);
                }
            }
        }
        return { match: matchArray, other: notMatchArray };
    }
    /**
     * It returns value of the input element.
     */
    getValue() {
        return this.input.value;
    }
    /**
     * It sets value of the input element.
     *
     * @param value
     */
    setValue(value) {
        this.input.value = value;
    }
    /*
     * Creates whole autocomplete element including autocomplete box description and listeners.
     */
    create() {
        // create form
        this.createForm();
        // create listeners
        this.createListeners();
        return this.formDiv;
    }
    /*
     * Creates input elements.
     */
    createForm() {
        // div for the whole autocomplete component
        this.formDiv = document.createElement('div');
        this.formDiv.classList.add(ID$a);
        // label div
        const labelDiv = document.createElement('div');
        labelDiv.classList.add(COMPONENT_DIV_LABEL_CLASS$2);
        labelDiv.innerHTML = this.getProps().label;
        // input div
        this.inputDiv = document.createElement('div');
        this.inputDiv.classList.add(COMPONENT_DIV_INPUT_CLASS$2);
        // input
        this.input = document.createElement('input');
        TabDOMUtil.setAttributes(this.input, ["class", "type", 'placeholder', 'type'], [COMPONENT_INPUT_CLASS$1, "text", this.placeholder || 'choose dimension: ', 'hidden']);
        // construct elements
        this.formDiv.appendChild(labelDiv);
        this.formDiv.appendChild(this.inputDiv);
        this.inputDiv.appendChild(this.input);
        return this.formDiv;
    }
    /**
     * Creates event listeners and creates/removes
     */
    createListeners() {
        if (this.input) {
            // when input changed, notify listeners
            this.input.onchange = this.getProps().onChangeAction;
            // input change listener: create autocomplete and find
            this.input.addEventListener('input', () => {
                this.createMenu();
            });
            // focus-in listener: create autocomplete
            this.input.addEventListener('focusin', () => {
                this.createMenu();
            });
            // key-up/down listener
            this.input.addEventListener('keydown', (e) => {
                var _a;
                if (this.completionListDiv != undefined) {
                    const completionItems = this.completionListDiv.children;
                    // remove active completion item
                    if (this.selectedCompletionItem != -1 && completionItems[this.selectedCompletionItem] != undefined) {
                        completionItems[this.selectedCompletionItem].classList.remove(COMPONENT_COMPLETION_ACTIVE_ITEM_CLASS);
                    }
                    // process key codes
                    if (e.key === "ArrowDown" || e.keyCode === 40) {
                        // key Arrow down -> next item (checks array overflow)
                        this.selectedCompletionItem = (this.selectedCompletionItem + 1) % completionItems.length;
                        e.preventDefault();
                    }
                    else if (e.key === "ArrowUp" || e.keyCode === 38) {
                        // key Arrow up -> previous item (checks array overflow)
                        if (this.selectedCompletionItem == -1) {
                            this.selectedCompletionItem = 0; // initial move
                        }
                        this.selectedCompletionItem = (this.selectedCompletionItem + completionItems.length - 1) % completionItems.length;
                        e.preventDefault();
                    }
                    else if ((e.key === "Enter" || e.keyCode === 13) && this.selectedCompletionItem !== -1) {
                        // key Enter
                        this.input.value = (_a = completionItems[this.selectedCompletionItem].textContent) !== null && _a !== void 0 ? _a : "";
                        this.completionListDiv.remove();
                        this.selectedCompletionItem != -1;
                        if (this.setData) {
                            this.setData(this.getValue());
                        }
                        else {
                            // input on change event needs to be invoked manualy in this case
                            this.input.dispatchEvent(new Event("change"));
                        }
                    }
                    else if (e.key === "Escape" || e.key === "Backspace" || e.keyCode === 27 || e.keyCode === 9) {
                        // key Escape or Tab
                        this.completionListDiv.remove();
                        this.selectedCompletionItem != -1;
                    }
                    // set new active item
                    if (completionItems[this.selectedCompletionItem] != undefined) {
                        completionItems[this.selectedCompletionItem].classList.add(COMPONENT_COMPLETION_ACTIVE_ITEM_CLASS);
                    }
                    return false;
                }
            }, false);
            // click litener: click outside the input
            document.addEventListener('click', (e) => {
                // note: there is one listener for every input
                if (this.completionListDiv != undefined && e.target != this.input) {
                    this.completionListDiv.remove();
                    this.selectedCompletionItem !== -1;
                }
            });
        }
    }
    /*
     * Creates and open MatchItems in PopUpList and set value according chosen input.
     */
    createMenu() {
        // if PopUpList already exist delete old one
        if (this.completionListDiv != undefined) {
            this.completionListDiv.remove();
        }
        // create completion list
        this.completionListDiv = document.createElement('div');
        this.completionListDiv.classList.add(COMPONENT_COMPLETION_LIST_CLASS);
        // help function which creates completion item
        const createCompletionItem = (label, className) => {
            // create item
            const completionItemDiv = document.createElement('div');
            completionItemDiv.innerHTML = label;
            completionItemDiv.classList.add(className);
            // add it to completion list div
            this.completionListDiv.appendChild(completionItemDiv);
            // on click listener: the item has been selected
            completionItemDiv.addEventListener('click', () => {
                this.input.value = completionItemDiv.textContent ? completionItemDiv.textContent : "";
                if (completionItemDiv.parentElement) {
                    completionItemDiv.parentElement.remove();
                }
                if (this.setData) {
                    this.setData(completionItemDiv.textContent || "");
                }
                else {
                    // input on change event needs to be invoked manualy in this case
                    this.input.dispatchEvent(new Event("change"));
                }
            });
        };
        // create new completion item for every matched and not matched path
        const currentOptions = this.getCurrentOptions();
        currentOptions.match.forEach(element => createCompletionItem(element, COMPONENT_COMPLETION_ITEM_CLASS));
        currentOptions.other.forEach(element => createCompletionItem(element, COMPONENT_COMPLETION_NOT_IN_CLASS));
        // append completion list
        this.inputDiv.appendChild(this.completionListDiv);
        // reset selection
        this.selectedCompletionItem = -1;
    }
}

const ID$9 = 'geovisto-input-checkbox';
/**
 * This class represents basic checkbox sidebar input.
 *
 * @author Krystof Rykala
 * @author Jiri Hynek - conversion to TypeScript
 */
class CheckboxSidebarInput extends AbstractMapFormInput {
    constructor(props) {
        super(props);
        // HTML elements
        this.div = undefined;
        this.input = undefined;
    }
    /**
     * Static function returns identifier of the input type.
     */
    static ID() {
        return ID$9;
    }
    /**
     * It returns input element.
     */
    create() {
        if (!this.div) {
            this.div = document.createElement('div');
            this.div.setAttribute('class', ID$9);
            const labelElement = this.createLabel();
            this.input = this.createCheckbox();
            this.div.appendChild(labelElement);
            this.div.appendChild(this.input);
        }
        return this.div;
    }
    /**
     * A help method which creates the label HTML element.
     */
    createLabel() {
        const props = this.getProps();
        const labelElement = document.createElement('div');
        labelElement.innerHTML = props.label;
        labelElement.setAttribute("class", `${ID$9}-label`);
        return labelElement;
    }
    /**
     * A help method which creates the checkbox HTML element.
     */
    createCheckbox() {
        const props = this.getProps();
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.onchange = props.onChangeAction;
        input.name = props.name;
        input.setAttribute("value", String(props.defaultValue || false));
        return input;
    }
    /**
     * It returns the value of the input element.
     */
    getValue() {
        var _a, _b;
        return (_b = (_a = this.input) === null || _a === void 0 ? void 0 : _a.checked) !== null && _b !== void 0 ? _b : false;
    }
    /**
     * It sets value of the input element.
     *
     * @param checked
     */
    setValue(checked) {
        if (this.input) {
            this.input.checked = checked;
        }
    }
    /*
     * Sets/removes attribute 'disabled' from input box.
     */
    setDisabled(disabled) {
        if (this.input) {
            if (disabled == true) {
                this.input.setAttribute("disabled", "true");
            }
            else {
                this.input.removeAttribute("disabled");
            }
        }
    }
}

const ID$8 = "geovisto-input-colorPicker";
const COMPONENT_DIV_LABEL_CLASS$1 = ID$8 + "-label";
const COMPONENT_DIV_INPUT_CLASS$1 = ID$8 + "-component";
const COMPONENT_VALUE_CLASS$1 = ID$8 + "-value";
/**
 * This class represents basic text sidebar input.
 *
 * @author Jakub Kachlik
 * @author Jiri Hynek - conversion to TypeScript
 *
 * TODO: replace this by LabeledTextFormInput
 */
class LabeledColorFormInput extends AbstractMapFormInput {
    constructor(props) {
        super(props);
        // HTML elements
        this.div = undefined;
        this.input = undefined;
    }
    /**
     * Static function returns identifier of the input type.
     */
    static ID() {
        return ID$8;
    }
    /**
     * It returns input element.
     */
    create() {
        if (!this.div) {
            const props = this.getProps();
            // div for the whole autocomplete component
            this.div = document.createElement('div');
            this.div.classList.add(ID$8);
            // label div
            const labelDiv = document.createElement('div');
            labelDiv.classList.add(COMPONENT_DIV_LABEL_CLASS$1);
            labelDiv.innerHTML = props.label;
            // input div
            const inputDiv = document.createElement('div');
            inputDiv.classList.add(COMPONENT_DIV_INPUT_CLASS$1);
            const valueDiv = document.createElement('div');
            valueDiv.setAttribute("class", COMPONENT_VALUE_CLASS$1);
            this.input = document.createElement("input");
            this.input.setAttribute("type", "color");
            this.input.setAttribute("id", ID$8);
            this.input.onchange = props.onChangeAction;
            // construct elements
            inputDiv.appendChild(this.input);
            inputDiv.appendChild(valueDiv);
            this.div.appendChild(labelDiv);
            this.div.appendChild(inputDiv);
        }
        return this.div;
    }
    /**
     * It returns value of the input element.
     */
    getValue() {
        var _a, _b;
        return (_b = (_a = this.input) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : "";
    }
    /**
     * It sets value of the input element.
     *
     * @param value
     */
    setValue(value) {
        if (this.input) {
            this.input.value = value;
        }
    }
    /*
     * Sets/removes attribute 'disabled' from input box.
     */
    setDisabled(disabled) {
        if (this.input) {
            if (disabled == true) {
                this.input.setAttribute("disabled", "true");
            }
            else {
                this.input.removeAttribute("disabled");
            }
        }
    }
}

const ID$7 = "geovisto-input-range";
const COMPONENT_DIV_LABEL_CLASS = ID$7 + "-label";
const COMPONENT_DIV_INPUT_CLASS = ID$7 + "-component";
const COMPONENT_VALUE_CLASS = ID$7 + "-value";
const COMPONENT_INPUT_CLASS = ID$7 + "-input";
/**
 * This class represents basic text sidebar input.
 *
 * @author Jakub Kachlik
 * @author Jiri Hynek - conversion to TypeScript
 */
class LabeledSliderFormInput extends AbstractMapFormInput {
    constructor(props) {
        super(props);
        // HTML elements
        this.div = undefined;
        this.input = undefined;
    }
    /**
     * Static function returns identifier of the input type.
     */
    static ID() {
        return ID$7;
    }
    /**
     * It returns input element.
     */
    create() {
        if (!this.div) {
            const props = this.getProps();
            // div for the whole autocomplete component
            this.div = document.createElement('div');
            this.div.classList.add(ID$7);
            // label div
            const labelDiv = document.createElement('div');
            labelDiv.classList.add(COMPONENT_DIV_LABEL_CLASS);
            labelDiv.innerHTML = props.label;
            // input div
            const inputDiv = document.createElement('div');
            inputDiv.classList.add(COMPONENT_DIV_INPUT_CLASS);
            const valueDiv = document.createElement('div');
            valueDiv.setAttribute("class", COMPONENT_VALUE_CLASS);
            valueDiv.innerText = props.defaultValue;
            this.input = document.createElement("input");
            this.input.setAttribute("type", "range");
            this.input.setAttribute("class", COMPONENT_INPUT_CLASS);
            this.input.setAttribute("min", props.minValue);
            this.input.setAttribute("max", props.maxValue);
            this.input.setAttribute("value", props.defaultValue);
            this.input.setAttribute("step", String(props.step || 1));
            this.input.onchange = props.onChangeAction;
            this.input.addEventListener('change', function () {
                valueDiv.innerText = this.value;
            });
            // construct elements
            inputDiv.appendChild(this.input);
            inputDiv.appendChild(valueDiv);
            this.div.appendChild(labelDiv);
            this.div.appendChild(inputDiv);
        }
        return this.div;
    }
    /**
     * It returns value of the input element.
     */
    getValue() {
        var _a, _b;
        return (_b = (_a = this.input) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : "";
    }
    /**
     * It sets value of the input element.
     *
     * @param value
     */
    setValue(value) {
        if (this.input) {
            this.input.value = value;
        }
    }
    /*
     * Sets/removes attribute 'disabled' from input box.
     */
    setDisabled(disabled) {
        if (this.input) {
            if (disabled == true) {
                this.input.setAttribute("disabled", "true");
            }
            else {
                this.input.removeAttribute("disabled");
            }
        }
    }
}

const ID$6 = "geovisto-input-text";
/**
 * This class represents basic text form input.
 *
 * @author Jiri Hynek
 * @author Krystof Rykala - generic input types
 */
class TextFormInput extends AbstractMapFormInput {
    constructor(props) {
        super(props);
    }
    /**
     * Static function returns identifier of the input type.
     */
    static ID() {
        return ID$6;
    }
    /**
     * It returns input element.
     */
    create() {
        var _a;
        if (this.input == undefined) {
            const props = this.getProps();
            this.input = document.createElement("input");
            this.input.setAttribute("class", `${ID$6}-input`);
            this.input.setAttribute("type", (_a = props.type) !== null && _a !== void 0 ? _a : "text");
            this.input.onchange = this.getProps().onChangeAction;
        }
        return this.input;
    }
    /**
     * It makes input element visible for the extended classes.
     */
    getInput() {
        return this.input;
    }
    /**
     * It returns value of the input element.
     */
    getValue() {
        return this.input ? this.input.value : "";
    }
    /**
     * It sets value of the input element.
     *
     * @param value
     */
    setValue(value) {
        if (this.input) {
            this.input.value = value;
        }
    }
    /*
     * Sets/removes attribute 'disabled' from input box.
     */
    setDisabled(disabled) {
        if (this.input) {
            if (disabled == true) {
                this.input.setAttribute("disabled", "true");
            }
            else {
                this.input.removeAttribute("disabled");
            }
        }
    }
}

const ID$5 = "geovisto-input-text-labeled";
/**
 * This class represents labeled text form input.
 *
 * @author Jiri Hynek
 * @author Krystof Rykala - input div wrapper
 */
class LabeledTextFormInput extends TextFormInput {
    constructor(props) {
        super(props);
        this.div = undefined;
    }
    /**
     * Static function returns identifier of the input type.
     */
    static ID() {
        return ID$5;
    }
    /**
     * It returns input element.
     */
    create() {
        if (this.div == undefined) {
            // create div block
            this.div = document.createElement("div");
            this.div.classList.add(ID$5);
            // append label
            this.div.appendChild(this.createLabel());
            // append input element
            const inputDiv = document.createElement("div");
            inputDiv.setAttribute("class", `${ID$5}-component`);
            inputDiv.appendChild(super.create());
            this.div.appendChild(inputDiv);
        }
        return this.div;
    }
    /**
     * A help method which creates the label HTML element.
     */
    createLabel() {
        const props = this.getProps();
        const labelElement = document.createElement("div");
        labelElement.innerHTML = props.label + ": ";
        labelElement.setAttribute("class", `${ID$5}-label`);
        return labelElement;
    }
}

/**
 * The interface declares functions for management of form inputs.
 *
 * @author Jiri Hynek
 */
class MapObjectForm {
    constructor(mapObject) {
        this.mapObject = mapObject;
    }
    getMapObject() {
        return this.mapObject;
    }
}

/**
 * The interface declares functions for management of form inputs.
 *
 * @author Jiri Hynek
 */
class MapLayerToolForm extends MapObjectForm {
    constructor(layerTool) {
        super(layerTool);
    }
    /**
     * Help method which returns a new universal autocomplete input for the any map domain dimension.
     *
     * @param dimension
     */
    getAutocompleteInput(dimension, formAction) {
        return new LabeledAutocompleteFormInput({
            label: dimension.getName(),
            options: dimension.getDomainManager().getDomainNames(),
            onChangeAction: (ev) => {
                this.getMapObject().updateDimension(dimension, ev.target.value, undefined);
                if (formAction) {
                    formAction(ev);
                }
            }
        });
    }
    /**
     * Help method which returns a new universal text input for the any map domain dimension.
     *
     * @param dimension
     */
    getTextInput(dimension, formAction) {
        return new LabeledTextFormInput({
            label: dimension.getName(),
            onChangeAction: (ev) => {
                this.getMapObject().updateDimension(dimension, ev.target.value, undefined);
                if (formAction) {
                    formAction(ev);
                }
            }
        });
    }
    /**
     * Help method which returns a new universal checkbox input for the any map boolean dimension.
     *
     * @param dimension
     */
    getCheckboxInput(dimension, formAction) {
        return new CheckboxSidebarInput({
            label: dimension.getName(),
            name: dimension.getName(),
            onChangeAction: (ev) => {
                this.getMapObject().updateDimension(dimension, new String(ev.target.checked).toString(), undefined);
                if (formAction) {
                    formAction(ev);
                }
            }
        });
    }
    /**
     * Help method which returns a new universal color input for the any map string dimension.
     *
     * @param dimension
     */
    getColorInput(dimension, formAction) {
        return new LabeledColorFormInput({
            label: dimension.getName(),
            onChangeAction: (ev) => {
                this.getMapObject().updateDimension(dimension, ev.target.value, undefined);
                if (formAction) {
                    formAction(ev);
                }
            }
        });
    }
    /**
     * Help method which returns a new universal slider input for the any map range integer dimension.
     *
     * @param dimension
     */
    getSliderInput(dimension, formAction) {
        return new LabeledSliderFormInput({
            label: dimension.getName(),
            defaultValue: new String(dimension.getValue()).toString(),
            minValue: new String(dimension.getTypeManager().getMinValue()).toString(),
            maxValue: new String(dimension.getTypeManager().getMaxValue()).toString(),
            onChangeAction: (ev) => {
                this.getMapObject().updateDimension(dimension, ev.target.value, undefined);
                if (formAction) {
                    formAction(ev);
                }
            }
        });
    }
    /**
     * Help method which returns a new universal number input for the any map number dimension.
     *
     * @param dimension
     */
    getNumberInput(dimension, formAction) {
        return new LabeledTextFormInput({
            label: dimension.getName(),
            type: "number",
            onChangeAction: (ev) => {
                this.getMapObject().updateDimension(dimension, ev.target.value, undefined);
                if (formAction) {
                    formAction(ev);
                }
            }
        });
    }
}

/**
 * The interface declares functions for management of legends.
 *
 * @author Tomas Koscielniak
 */
class MapObjectLegend {
    constructor(mapObject) {
        this.mapObject = mapObject;
    }
    getMapObject() {
        return this.mapObject;
    }
}

/**
 * The interface declares functions for management of legends.
 *
 * @author Tomas Koscielniak
 */
class MapLayerToolLegend extends MapObjectLegend {
    constructor(layerTool) {
        super(layerTool);
    }
}

const ID$4 = "geovisto-input-filter-autocomplete";
const COMPONENT_DIV_CLASS$1 = ID$4;
/**
 * This class represents a form input composed of three autocomplete inputs used for filters.
 *
 * @author Jakub Kachlik
 * @author Jiri Hynek (refactoring, code review)
 */
class FilterAutocompleteFormInput extends AbstractMapFormInput {
    constructor(props) {
        super(props);
        // inputs
        this.inputDiv = null;
        this.input = null;
    }
    /**
     * Static function returns identifier of the input type
     */
    static ID() {
        return ID$4;
    }
    /**
     * It returns filter div element composed of 3 autocomplete form inputs.
     */
    create() {
        if (this.inputDiv == undefined) {
            // create parent div
            this.inputDiv = document.createElement("div");
            this.inputDiv.classList.add(COMPONENT_DIV_CLASS$1);
            // initialize filter inputs
            const props = this.getProps();
            this.input = {
                val: new LabeledAutocompleteFormInput({ label: "Value", options: [], onChangeAction: props.vals.onChangeAction }),
                op: new LabeledAutocompleteFormInput({ label: "Operation", options: props.ops.options, onChangeAction: props.ops.onChangeAction }),
                data: new LabeledAutocompleteFormInput({ label: "Data", options: props.data.options, onChangeAction: props.data.onChangeAction })
            };
            // create elements of filter inputs and add them to parent div
            this.inputDiv.appendChild(this.input.data.create());
            this.inputDiv.appendChild(this.input.op.create());
            this.inputDiv.appendChild(this.input.val.create());
        }
        return this.inputDiv;
    }
    /**
     * It returns values of the inputs.
     */
    getValue() {
        return this.input ? {
            data: this.input.data.getValue(),
            op: this.input.op.getValue(),
            val: this.input.val.getValue(),
        } : {
            data: "",
            op: "",
            val: ""
        };
    }
    /**
     * It sets values of the inputs.
     *
     * @param value
     */
    setValue(value) {
        if (this.input) {
            this.input.data.setValue(value.data);
            this.input.op.setValue(value.op);
            this.input.val.setValue(value.val);
        }
    }
    /*
     * Sets/removes attribute 'disabled' from input box.
     */
    setDisabled(disabled) {
        if (this.input) {
            this.input.data.setDisabled(disabled);
            this.input.op.setDisabled(disabled);
            this.input.val.setDisabled(disabled);
        }
    }
    /**
     * It returns input
     */
    getInputElement() {
        return this.input;
    }
}

const ID$3 = "geovisto-input-filter-select";
const COMPONENT_DIV_CLASS = ID$3;
/**
 * This class represents a map input composed of three selects used for filters.
 *
 * @author Jiri Hynek
 */
class FilterSelectFormInput extends AbstractMapFormInput {
    constructor(props) {
        super(props);
        // inputs
        this.input = {
            data: undefined,
            op: undefined,
            val: undefined,
        };
    }
    /**
     * Static function returns identifier of the input type
     */
    static ID() {
        return ID$3;
    }
    /**
     * It returns filter div element composed of 2 selects and one input element.
     */
    create() {
        if (this.inputDiv == undefined) {
            const props = this.getProps();
            // create inline container
            this.inputDiv = document.createElement("div");
            this.inputDiv.classList.add(COMPONENT_DIV_CLASS);
            this.input.val = this.createInputElement();
            this.input.op = this.createSelectElement(function () { }, props.ops.options);
            this.input.data = this.createSelectElement(function () { }, props.data.options);
            // data select element
            this.inputDiv.appendChild(this.input.data);
            // data op element
            this.inputDiv.appendChild(this.input.op);
            // data val element
            this.inputDiv.appendChild(this.input.val);
        }
        return this.inputDiv;
    }
    /**
     * Help static function which creates select element.
     *
     * @param onChangeAction
     * @param options
     */
    createSelectElement(onChangeAction, options) {
        // create select element
        const select = document.createElement('select');
        select.onchange = onChangeAction;
        // append options
        let option;
        for (let i = 0; i < options.length; i++) {
            option = select.appendChild(document.createElement("option"));
            option.setAttribute("value", options[i]);
            option.innerHTML = options[i];
        }
        return select;
    }
    /**
     * Help static function which creates select element.
     */
    createInputElement() {
        // create input element
        const input = document.createElement('input');
        input.setAttribute("type", "text");
        input.setAttribute("size", "10");
        return input;
    }
    /**
     * It returns values of the inputs.
     */
    getValue() {
        return {
            data: this.input.data ? this.input.data.value : "",
            op: this.input.op ? this.input.op.value : "",
            val: this.input.val ? this.input.val.value : "",
        };
    }
    /*
     * Sets/removes attribute 'disabled' from input box.
     */
    setDisabled(disabled) {
        var _a, _b, _c, _d, _e, _f;
        if (this.input) {
            if (disabled == true) {
                (_a = this.input.data) === null || _a === void 0 ? void 0 : _a.setAttribute("disabled", "true");
                (_b = this.input.op) === null || _b === void 0 ? void 0 : _b.setAttribute("disabled", "true");
                (_c = this.input.val) === null || _c === void 0 ? void 0 : _c.setAttribute("disabled", "true");
            }
            else {
                (_d = this.input.data) === null || _d === void 0 ? void 0 : _d.removeAttribute("disabled");
                (_e = this.input.op) === null || _e === void 0 ? void 0 : _e.removeAttribute("disabled");
                (_f = this.input.val) === null || _f === void 0 ? void 0 : _f.removeAttribute("disabled");
            }
        }
    }
    /**
     * It sets values of the inputs.
     *
     * @param value
     */
    setValue(value) {
        if (this.input.data && this.input.op && this.input.val) {
            this.input.data.value = value.data;
            this.input.op.value = value.op;
            this.input.val.value = value.val;
        }
    }
}

const ID$2 = "geovisto-input-select";
/**
 * This class represents a basic select form input composed of options.
 *
 * @author Jiri Hynek
 */
class SelectFormInput extends AbstractMapFormInput {
    constructor(props) {
        super(props);
    }
    /**
     * Static function returns identifier of the input type
     */
    static ID() {
        return ID$2;
    }
    /**
     * It returns select element.
     */
    create() {
        if (this.element == undefined) {
            const props = this.getProps();
            // create select element
            this.element = document.createElement('select');
            this.element.onchange = props.onChangeAction;
            // append options
            let option;
            const options = props.options;
            for (let i = 0; i < options.length; i++) {
                option = this.element.appendChild(document.createElement("option"));
                option.setAttribute("value", options[i]);
                option.innerHTML = options[i];
            }
        }
        return this.element;
    }
    /**
     * It returns value of the select element.
     */
    getValue() {
        return this.element ? this.element.value : "";
    }
    /**
     * It sets value of the select element.
     *
     * @param value
     */
    setValue(value) {
        if (this.element) {
            this.element.value = value;
        }
    }
    /*
     * Sets/removes attribute 'disabled' from input box.
     */
    setDisabled(disabled) {
        if (this.element) {
            if (disabled == true) {
                this.element.setAttribute("disabled", "true");
            }
            else {
                this.element.removeAttribute("disabled");
            }
        }
    }
}

const ID$1 = "geovisto-input-text-area-labeled";
/**
 * This class represents basic text sidebar input.
 *
 * @author Andrej Tlcina
 */
class TextAreaFormInput extends AbstractMapFormInput {
    constructor(props) {
        super(props);
        this.label = props.label || "";
        this.value = props.value || "";
        this.onChange = props.onChangeAction || null;
        this.div = undefined;
    }
    /**
     * Static function returns identifier of the input type.
     */
    static ID() {
        return ID$1;
    }
    /**
     * It returns input element.
     */
    create() {
        if (this.div === undefined) {
            this.input = document.createElement("textarea");
            // create div block
            this.div = document.createElement("div");
            this.div.classList.add("textarea-wrapper");
            // append label
            if (this.label) {
                this.div.appendChild(document.createTextNode(this.label + ": "));
            }
            if (this.value) {
                this.input.value = this.value;
            }
            // append input element
            this.div.appendChild(this.input);
            this.input.onchange = this.action;
        }
        return this.div;
    }
    action(e) {
        const val = e.target.value;
        this.setValue(val);
        if (this.onChange)
            this.onChange(val);
    }
    /**
     * It makes input element visible for the extended classes.
     */
    getInput() {
        return this.input;
    }
    /**
     * It returns value of the input element.
     */
    getValue() {
        return this.input.value;
    }
    /**
     * It sets value of the input element.
     */
    setValue(value) {
        this.input.value = value;
    }
    setDisabled(disabled) {
        if (this.input) {
            if (disabled) {
                this.input.setAttribute("disabled", "true");
            }
            else {
                this.input.removeAttribute("disabled");
            }
        }
    }
}

/**
 * This class provides a factory for form inputs.
 *
 * @author Jiri Hynek
 */
class MapFormInputFactory {
    /**
     * It creates the text form input.
     */
    text(props) {
        return new TextFormInput(props);
    }
    /**
     * It creates the select form input.
     */
    select(props) {
        return new SelectFormInput(props);
    }
    /**
     * It creates the labeled text form input.
     */
    labeledText(props) {
        return new TextFormInput(props);
    }
    /**
     * It creates the labeled select form input.
     */
    labeledSelect(props) {
        return new SelectFormInput(props);
    }
    /**
     * It creates the labeled autocomplete form input.
     */
    labeledAutocomplete(props) {
        return new LabeledAutocompleteFormInput(props);
    }
    /**
     * It creates the filter select form input.
     */
    filterSelect(props) {
        return new FilterSelectFormInput(props);
    }
    /**
     * It creates the filter autocomplete form input.
     */
    filterAutocomplete(props) {
        return new FilterAutocompleteFormInput(props);
    }
    /**
     * It creates the labeled slider form input.
     */
    labeledSlider(props) {
        return new LabeledSliderFormInput(props);
    }
    /**
     * It creates the labeled color form input.
     */
    labeledColor(props) {
        return new LabeledColorFormInput(props);
    }
    /**
     * It creates the labeled checkbox form input.
     */
    labeledCheckbox(props) {
        return new CheckboxSidebarInput(props);
    }
    /**
     * It creates the textarea form input.
     */
    textArea(props) {
        return new TextAreaFormInput(props);
    }
}

const ID = "geovisto-input-select-labeled";
/**
 * This class represents basic select form input composed of options.
 *
 * @author Jiri Hynek
 */
class LabeledSelectFormInput extends SelectFormInput {
    constructor(props) {
        super(props);
        this.div = undefined;
    }
    /**
     * Static function returns identifier of the input type
     */
    static ID() {
        return ID;
    }
    /**
     * It returns select element.
     */
    create() {
        if (this.div == undefined) {
            // create select element
            const input = super.create();
            // create div block
            this.div = document.createElement("div");
            // append label
            const props = this.getProps();
            if (props.label != undefined) {
                this.div.appendChild(document.createTextNode(props.label + ": "));
            }
            // append select element
            this.div.appendChild(input);
        }
        return this.div;
    }
}

/**
 * This class provide functions which return the default state values.
 *
 * @author Jiri Hynek
 */
class MapToolDefaults extends MapObjectDefaults {
    /**
     * It returns default map data manager.
     */
    getDataManager() {
        return (new MapAggregationFunctionFactory$1).json([]);
    }
    /**
     * It returns default geo data manager.
     */
    getGeoDataManager(geoDataArray) {
        return new GeoDataManager(geoDataArray !== null && geoDataArray !== void 0 ? geoDataArray : []);
    }
    /**
     * By defaults it returns the config with undefined props.
     */
    getConfig() {
        const config = super.getConfig();
        config.enabled = undefined;
        return config;
    }
    /**
     * By default, the tool is singleton
     */
    isSingleton() {
        return false;
    }
    /**
     * By default, the tool is enabled.
     */
    isEnabled() {
        return true;
    }
    /**
     * It returns the label of the tool.
     */
    getLabel() {
        return "Tool";
    }
    /**
     * It returns the icon of the tool.
     */
    getIcon() {
        return '<i class="fa fa-file"></i>';
    }
}

/**
 * This class provide functions which return the default state values.
 *
 * @author Jiri Hynek
 */
class LayerToolDefaults extends MapToolDefaults {
    /**
     * It returns a unique type string of the tool which is based on the layer it wraps.
     */
    getType() {
        return LayerToolDefaults.TYPE;
    }
    /**
     * It returns the layer name.
     */
    getLayerName() {
        return "Abstract layer";
    }
    /**
     * It returns the default layer tool dimensions.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getDimensions(map) {
        return {};
    }
}
/**
 * A unique string of the tool type.
 */
LayerToolDefaults.TYPE = "geovisto-tool-layer-abstract";

/**
 * This class manages state of the tool.
 * It wraps the state since the tool can work with state objects which needs to be explicitly serialized.
 *
 * @author Jiri Hynek
 */
class MapToolState extends AbstractMapObjectState {
    /**
     * It creates a map object state.
     *
     * @param tool
     */
    constructor(tool) {
        super(tool);
    }
    /**
     * It resets the state to the initial props.
     *
     * @param defaults
     * @param props
     * @param initProps
     */
    initialize(defaults, props, initProps) {
        // it sets the map
        this.setMap(initProps.map);
        // set the enabled property 
        this.setEnabled(props.enabled == undefined ? defaults.isEnabled() : props.enabled);
        // set the label property 
        this.setLabel(props.label == undefined ? defaults.getLabel() : props.label);
        // set the icon property 
        this.setLabel(props.icon == undefined ? defaults.getIcon() : props.icon);
        // set super props
        super.initialize(defaults, props, initProps);
    }
    /**
     * The metod takes config and deserializes the values.
     *
     * @param config
     */
    deserialize(config) {
        super.deserialize(config);
        // tools properties
        if (config.enabled != undefined)
            this.setEnabled(config.enabled);
    }
    /**
     * The method serializes the tool state. Optionally, a serialized value can be let undefined if it equals the default value.
     *
     * @param defaults
     */
    serialize(defaults) {
        const config = super.serialize(defaults);
        // tools properties
        config.enabled = defaults && this.isEnabled() == defaults.isEnabled() ? undefined : this.isEnabled();
        return config;
    }
    /**
     * It returns the enabled property of the tool state.
     */
    isEnabled() {
        return this.enabled;
    }
    /**
     * It sets the enabled property of tool state.
     *
     * @param enabled
     */
    setEnabled(enabled) {
        this.enabled = enabled;
    }
    /**
     * It returns the label property of the tool state.
     */
    getLabel() {
        return this.label;
    }
    /**
     * It sets the label property of the tool state.
     *
     * @param label
     */
    setLabel(label) {
        this.label = label;
    }
    /**
     * It returns the icon property of the tool state.
     */
    getIcon() {
        return this.icon;
    }
    /**
     * It sets the icon property of the tool state.
     *
     * @param icon
     */
    setIcon(icon) {
        this.icon = icon;
    }
    /**
     * It returns the map property of the tool state.
     */
    getMap() {
        return this.map;
    }
    /**
     * It sets the map property of the tool state.
     *
     * @param map
     */
    setMap(map) {
        this.map = map;
    }
}

/**
 * This class provide functions for using the state of the layer tool.
 *
 * @author Jiri Hynek
 */
class LayerToolState extends MapToolState {
    /**
     * It creates a tool state.
     */
    constructor(tool) {
        super(tool);
    }
    /**
     * It resets the state with respect to the initial props.
     *
     * @param defaults
     * @param props
     * @param initProps
     */
    initialize(defaults, props, initProps) {
        // the layer tool properties
        this.setLayerName(props.name == undefined ? defaults.getLayerName() : props.name);
        this.setDimensions(defaults.getDimensions(initProps.map));
        // set super props
        super.initialize(defaults, props, initProps);
    }
    /**
     * The metod takes config and deserializes the values.
     *
     * @param config
     */
    deserialize(config) {
        super.deserialize(config);
        // the layer tool config
        if (config.name != undefined)
            this.setLayerName(config.name);
        // the layer tool config
        if (config.data != undefined) {
            this.deserializeDimensions(config.data);
        }
    }
    /**
     * It sets the marker layer dimensions property of tool state.
     *
     * @param dimensionsConfig
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    deserializeDimensions(dimensionsConfig) {
        return;
    }
    /**
     * The method serializes the tool state. Optionally, defaults can be set if property is undefined.
     *
     * @param defaults
     */
    serialize(defaults) {
        const config = super.serialize(defaults);
        // serialize the layer tool properties
        config.name = defaults && this.getLayerName() == defaults.getLayerName() ? undefined : this.getLayerName();
        return config;
    }
    /**
     * It returns the layer name property of the tool state.
     */
    getLayerName() {
        return this.layerName;
    }
    /**
     * It sets the layer name property of the tool state.
     *
     * @param layerName
     */
    setLayerName(layerName) {
        this.layerName = layerName;
    }
    /**
     * It returns the layer items property of the tool state.
     */
    getLayerItems() {
        return this.layerItems;
    }
    /**
     * It sets the layer items property of tool state.
     *
     * @param layerItems
     */
    setLayerItems(layerItems) {
        this.layerItems = layerItems;
    }
    /**
     * It returns the map layer dimensions property of the tool state.
     */
    getDimensions() {
        return this.dimensions;
    }
    /**
     * It sets the map layer dimensions property of tool state.
     *
     * @param dimensions
     */
    setDimensions(dimensions) {
        this.dimensions = dimensions;
    }
}

/**
 * This class provides basic tools API.
 *
 * @author Jiri Hynek
 */
class MapTool extends MapObject {
    /**
     * It initializes the tool.
     */
    constructor(props) {
        super(props);
    }
    /**
     * It creates copy of the uninitialized tool.
     */
    copy() {
        return new MapTool(this.getProps());
    }
    /**
     * Help function which returns the props given by the programmer.
     */
    getProps() {
        return super.getProps();
    }
    /**
     * It returns default values of the state properties.
     */
    getDefaults() {
        return super.getDefaults();
    }
    /**
     * It creates new defaults of the tool.
     */
    createDefaults() {
        return new MapToolDefaults();
    }
    /**
     * It returns the map tool state.
     */
    getState() {
        return super.getState();
    }
    /**
     * It creates new defaults of the tool.
     */
    createState() {
        return new MapToolState(this);
    }
    /**
     * It returns the tool API
     */
    getAPIGetter() {
        return undefined;
    }
    /**
     * Help getter which returns a logtical value whether the tool type is singleton.
     */
    isSingleton() {
        return this.getDefaults().isSingleton();
    }
    /**
     * It initializes the tool before it is created.
     * It processes the serialized config and sets the Geovisto map which manages the tools.
     *
     * This cannot be done in the tool constructor
     * since the tool can be created before the Geovisto map is created.
     *
     * This cannot be done in the tool create function
     * since there can be possible dependencies between the tools
     * (the tool might depend on other tools which needs to be initialized).
     *
     * @param initProps
     */
    initialize(initProps) {
        var _a;
        super.initialize(initProps);
        // register this tool as a listener to map events
        (_a = this.getMap()) === null || _a === void 0 ? void 0 : _a.getState().getEventManager().addEventListener(this);
        return this;
    }
    /**
     * Help function returns map which uses this tool.
     *
     * Do not override this function. Use the state class instead.
     *
     * @returns {CombinedMap}
     */
    getMap() {
        return this.getState().getMap();
    }
    /**
     * It creates a tool.
     *
     * Override this function.
     */
    create() {
        return this;
    }
    /**
     * Help getter which returns enabled property of state.
     *
     * Do not override this function. Use the state class instead.
     */
    isEnabled() {
        return this.getState().isEnabled();
    }
    /**
     * Some tools might be dynamicaly enabled/disabled.
     * This function is called externally when the tool is enabled/disabled.
     *
     * Override this function, if needed.
     *
     * @param enabled
     */
    setEnabled(enabled) {
        var _a;
        if (this.isEnabled() != enabled) {
            this.getState().setEnabled(enabled);
        }
        // notify listeners
        (_a = this.getState().getMap()) === null || _a === void 0 ? void 0 : _a.getState().getEventManager().scheduleEvent(new ToolEnabledEvent(this, enabled), undefined, undefined);
    }
    /**
     * Help function which switches enabled state (enabled/disabled).
     *
     * Do not override this function. Use setEnabled instead.
     */
    switchEnabled() {
        // update settings
        this.setEnabled(!this.isEnabled());
    }
    /**
     * This function is called when a custom event is invoked.
     *
     * Override this function, if needed.
     *
     * @param event
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleEvent(event) {
        return;
    }
}

/**
 * This class wraps filter tool. It provides methods for layer management.
 *
 * @author Jiri Hynek
 */
class AbstractLayerTool extends MapTool {
    /**
     * It creates a new tool with respect to the props.
     *
     * @param props
     */
    constructor(props) {
        super(props);
    }
    /**
     * Help function which returns the props given by the programmer.
     */
    getProps() {
        return super.getProps();
    }
    /**
     * It returns default values of the state properties.
     */
    getDefaults() {
        return super.getDefaults();
    }
    /**
     * It creates new defaults of the layer tool.
     */
    createDefaults() {
        return new LayerToolDefaults();
    }
    /**
     * It returns the layer tool state.
     */
    getState() {
        return super.getState();
    }
    /**
     * It creates new defaults of the layer tool.
     */
    createState() {
        return new LayerToolState(this);
    }
    /**
     * It initializes the state of the layer tool.
     * It processes the serialized config and sets further objects.
     *
     * This cannot be done in the object constructor
     * since the object can be created before the Geovisto map is created.
     *
     * @param initProps
     */
    initialize(initProps) {
        return super.initialize(initProps);
    }
    /**
     * It creates new layer with respect to configuration
     */
    create() {
        if (this.isEnabled()) {
            this.showLayerItems();
        }
        return this;
    }
    /**
     * It changes layer state to enabled/disabled.
     *
     * @param enabled
     */
    setEnabled(enabled) {
        var _a;
        if (enabled != this.isEnabled()) {
            // update state
            this.getState().setEnabled(enabled);
            // show ot hide the layer
            if (enabled) {
                this.showLayerItems();
            }
            else {
                this.hideLayerItems();
            }
        }
        // notify listeners
        (_a = this.getState().getMap()) === null || _a === void 0 ? void 0 : _a.getState().getEventManager().scheduleEvent(new ToolEnabledEvent(this, enabled), undefined, undefined);
    }
    /**
     * Help function which shows layer items.
     *
     * This function is meant to be private.
     */
    showLayerItems() {
        var _a;
        const leafletMap = (_a = this.getMap()) === null || _a === void 0 ? void 0 : _a.getState().getLeafletMap();
        if (leafletMap) {
            // get/create items
            const layerItems = this.getLayerItems();
            // render/remove items
            for (let j = 0; j < layerItems.length; j++) {
                layerItems[j].addTo(leafletMap);
            }
            // render data
            this.render(LayerToolRenderType$1.DATA);
        }
    }
    /**
     * Help function which hides layer items
     *
     * This function is meant to be private.
     */
    hideLayerItems() {
        var _a;
        const leafletMap = (_a = this.getMap()) === null || _a === void 0 ? void 0 : _a.getState().getLeafletMap();
        // render/remove items
        if (leafletMap) {
            // get/create items
            const layerItems = this.getLayerItems();
            for (let j = 0; j < layerItems.length; j++) {
                leafletMap.removeLayer(layerItems[j]);
            }
        }
    }
    /**
     * It returns layer items which should be rendered.
     */
    getLayerItems() {
        let layerItems = this.getState().getLayerItems();
        if (layerItems == undefined) {
            layerItems = this.createLayerItems();
            // update state
            this.getState().setLayerItems(layerItems);
        }
        return layerItems;
    }
    /**
     * It reloads data and redraw the layer with respect to the type.
     *
     * By default it works with LayerRedrawType
     *
     * @param type
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render(type, animateOptions) {
        var _a;
        // notify listeners
        (_a = this.getState().getMap()) === null || _a === void 0 ? void 0 : _a.getState().getEventManager().scheduleEvent(new LayerToolRenderedEvent(this, type), undefined, undefined);
        return;
    }
    /**
     * It updates the dimension.
     *
     * @param dimension
     * @param value
     * @param renderType
     */
    updateDimension(dimension, value, renderType = undefined) {
        var _a;
        // get selected values and update layer tool's dimension
        const domain = dimension.findValue(value);
        if (dimension.getValue() !== domain) {
            dimension.setStringValue(value);
            if (renderType != undefined) {
                this.render(renderType);
            }
            // notify listeners
            (_a = this.getState().getMap()) === null || _a === void 0 ? void 0 : _a.getState().getEventManager().scheduleEvent(new LayerToolDimensionChangeEvent(this), undefined, undefined);
        }
    }
}

/**
 * It rounds number to specified precision
 * @param roundNumber number to be rounded
 * @param roundTo precision
 */
function roundValues(roundNumber, roundTo) {
    // Convert to number if needed
    if (typeof roundNumber == "string") {
        roundNumber = +roundNumber;
    }
    if (typeof roundTo == "string") {
        roundTo = +roundTo;
    }
    const factor = Math.pow(10, roundTo);
    roundNumber = Math.round(roundNumber * factor) / factor;
    return roundNumber;
}

export { AbstractLayerTool, AbstractMapConfigManager, AbstractMapDataDomain, AbstractMapDataManager, AbstractMapDimension, AbstractMapDomain, AbstractMapFormInput, BooleanTypeManager, CountAggregationFunction, DataChangeEvent, DataManagerChangeEvent, FilterAutocompleteFormInput, FilterSelectFormInput, GeoDataChangeEvent, GeoDataFactory, GeoDataManager, GeoDataManagerChangeEvent, GeoJSONTypes$1 as GeoJSONTypes, GeoJsonData, Geovisto, GeovistoMap, GeovistoMapDefaults, GeovistoMapState, IntegerRangeManager, IntegerTypeManager, JsonMapDataDomain, JsonMapDataManager, LabeledAutocompleteFormInput, CheckboxSidebarInput as LabeledCheckboxFormInput, LabeledColorFormInput, LabeledSelectFormInput, LabeledSliderFormInput, LabeledTextFormInput, LayerToolDefaults, LayerToolDimensionChangeEvent, LayerToolRenderType$1 as LayerToolRenderType, LayerToolRenderedEvent, LayerToolState, MapAggregationFunction, MapAggregationFunctionFactory$2 as MapAggregationFunctionFactory, MapChangeEvent, MapConfigManager, MapConfigManagerFactory, MapAggregationFunctionFactory$1 as MapDataManagerFactory, MapDomain, MapDomainArrayManager, MapDomainDimension$1 as MapDomainDimension, MapAggregationFunctionFactory as MapDomainManagerFactory, MapDynamicDomainDimension, MapEvent, MapEventFactory, MapFormInputFactory, MapLayerToolForm, MapLayerToolLegend, MapObject, MapObjectDefaults, MapObjectForm, MapObjectLegend, AbstractMapObjectState as MapObjectState, MapObjectsManager, MapTool, MapToolDefaults, MapToolState, MapToolsManager, MapDomainDimension as MapTypeDimension, SelectFormInput, StringTypeManager, SumAggregationFunction, TabDOMUtil, TextFormInput, ToolEnabledEvent, instanceOfMapForm, instanceOfMapLegend, roundValues };
//# sourceMappingURL=index.es.js.map
